// Stockholm, "Advent snow over Stadshuset". An early-December night with light snow falling, seen from Evert Taubes
// terrass on the west shore of Riddarholmen, looking west to north-west over the open, dark water of Riddarfjärden
// (Stadshuset's tower bears about 300 degrees, 500 m off; Västerbron about 267 degrees, 2.3 km off). Left to right, as
// seen from there: Södermalm's cliff over Söder Mälarstrand (old houses on the quay, Mariaberget and Monteliusvägen on
// top, a rooftop neon sign), the trees of Långholmen and Västerbron's two arches far to the west, Kungsholmen's houses
// along Norr Mälarstrand, then Stadshuset at the tip of Kungsholmen. Its south-east corner is the one nearest to us,
// and that is where the square brick tower stands, right at the shore: the south wing with its arcade along the water
// recedes to the left, the east wing (the council chamber's tall windows, the small tower with its gilded dome) to the
// right, towards Stadshusbron, where a white archipelago steamboat lies moored for the Christmas cruises. The tower:
// a floodlit brick shaft to the gallery at 73 m, the open copper lantern, the cupola and the three gilded crowns at
// 106 m, turned towards Gamla Stan and so towards us. Windows glow with Advent stars and seven-candle candlesticks.
// The foreground is the terrace's snowy granite edge, two lamp posts, Evert Taube in bronze with his lute, and the
// people who came to look at the view.
// render(t, { crowd }) thins the people on the terrace and the quays; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds):
//   10-62     the commuter boat crosses from Söder Mälarstrand towards Kungsholmen (horn at 11)
//   18-58     a dog walker crosses the terrace, left to right; at 33-39 the dog stops to sniff the snow
//   30-46     the couple at the edge: she points up at the tower
//   44-60     the child catches snowflakes, arms up, jumping for them
//   60-92     a parent pulls a child across the terrace on a red pulka, right to left
//   64-154    a dark-hulled steamboat leaves Stadshusbron on a Christmas cruise and sails west, lit stem to stern (horn at 66)
//   96-112    a jogger in a bobble hat runs by, right to left, breath steaming
//   117-170   a gull flies in and stands on Evert Taube's head (120-166), then flies off
//   128-180   the commuter boat comes back
//   138-178   the dog walker comes back, the dog sniffing again at 153-159
//   150-184   the pulka comes back, left to right
//   180-186   the tower's bells strike three; the lantern brightens and rings widen from the belfry
//   198-214   the jogger runs back
//   204-232   the child catches snowflakes again; the couple turn to each other and kiss at 222-228
//   all loop  three layers of cloud drift and veil the moon, the snow falls at three depths, windows switch on and off,
//             cars cross Västerbron and the quays, gulls wheel round the floodlit tower, the photographer at his tripod
//             exposes for 14 s and checks the shot every 24 s, the couple lean together every 40 s, a man on the
//             bench sips from his thermos every 20 s, the neon mug steams, swans and ducks drift by the edge, the
//             moored steamboat's lights twinkle and its funnel smokes, the snow glitters under the lamps
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 162;                                     // the far waterline: everything across the water stands on it
const QY = 199;                                     // the terrace's snowy edge: the water ends above this row
const EDGE = 204;                                   // feet of the people standing at the edge
const LANE_DOG = 230, LANE_RUN = 247;               // feet rows of the walkers crossing the terrace
// Stadshuset's tower, seen across its south-east corner: the columns of its corners (south-west, south-east = the
// corner nearest to us, north-east), its axis, the crowns' top row and the top of the brick shaft
const T = { sw: 308, se: 318, ne: 336, cx: 322, top: 22, shaft: 68 };
const LAN = { sw: 313, se: 319, ne: 331, top: 45, bot: 61 };   // the open copper lantern on the gallery
const SWING = { x0: 240, x1: 307 }, EWING = { x0: 337, x1: 405 };   // the south wing (left) and the east wing (right)
const EAVE = 32, RIDGE = 43, PERSP = 0.12;          // the wings' eaves and ridge above the water at the tower; far ends shrink by PERSP
const SMALL = { x0: 386, x1: 396, cx: 391, top: 88 };   // the small tower on the east wing, its gilded dome
const BB = { x: 60, y: 68, w: 46, h: 20 };          // the rooftop neon sign on Södermalm (the city's billboard)
const BB_ROOF = 99;                                 // the roof its frame stands on
const LAMPS = [{ x: 22, y: 124 }, { x: 456, y: 124 }];   // the terrace's two lamp posts (lantern centre)
const STATUE = { x: 84, fy: 199 };                  // Evert Taube: the feet on his plinth
const MOON = { x: 428, y: 40 };
const ACCENT = '#5fb3a0';                           // Stadshuset's copper verdigris

/* ================= Timeline ================= */
const FERRY = [{ t0: 10, t1: 62, dir: 1 }, { t0: 128, t1: 180, dir: -1 }];
const STEAMER = { t0: 64, t1: 154, horn: 66 };
const DOGW = [{ t0: 18, t1: 58, dir: 1 }, { t0: 138, t1: 178, dir: -1 }];
const JOG = [{ t0: 96, t1: 112, dir: -1 }, { t0: 198, t1: 214, dir: 1 }];
const BELLS = [180, 183, 186];
const GULL_SIT = { land: 120, off: 166 };
const POINT = [30, 46], KISS = [222, 228];
const CATCH = [[44, 60], [204, 232]];
// The people the scene shows by default (all of them: there are few). render(t, { crowd }) thins them by rank; crowd 0
// is the visitors' "None": nobody at all, and what is theirs goes with them (the tripod, the dog, the thermos, the
// sledge). The statue stays, and so do the boats, with nobody but the skipper aboard.
const CROWD = 1;
const nobody = crowd => crowd === 0;
const RANK = { couple: 0, kid: 0, photo: 0.62, bench: 0.42, dog: 0.3, jog: 0.78, pulka: 0.55 };
const PULKA = [{ t0: 60, t1: 92, dir: -1 }, { t0: 150, t1: 184, dir: 1 }];   // a parent pulls a child across on a sledge

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const box = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
/* ================= The sky: a low snow sky, the city's light under it, a moon veiled by thin cloud ================= */
const SKY = ['#080b1a', '#0b0f20', '#0f1427', '#13192e', '#181e36', '#1e243e', '#252a46', '#2d304d', '#373654', '#423c59',
  '#4e435c', '#5b4b5e', '#68525f'];
function renderSky() {
  const p = pix(W, WL);
  for (let y = 0; y < WL; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(y / WL, 1.3) * 0.74;
    v += 0.2 * Math.exp(-(((x - 300) / 240) ** 2)) * smooth(70, 160, y);                 // the city's glow under the clouds
    v += 0.16 * Math.exp(-(((x - 160) / 70) ** 2)) * smooth(118, 158, y);                 // the haze over the water to the west
    v += 0.14 * Math.max(0, 1 - Math.hypot((x - MOON.x) / 120, (y - MOON.y) / 70));      // moonlight in the haze
    v += (vnoise(x / 70, y / 10, 3) - 0.5) * 0.05;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
function renderMoon() {
  const r = 6, s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, d = Math.hypot(dx, dy);
    if (d > r + 0.3) continue;
    let col = dx + dy < -4 ? '#f2eee8' : '#d6d2dc';
    if (d > r - 1.1) col = dx + dy > 0 ? '#a8a4bc' : '#c4c0d0';
    if (Math.hypot(dx + 2, dy - 1) < 1.5 || Math.hypot(dx - 2, dy + 2) < 1.1) col = '#b4b0c4';
    p.set(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(77);
  while (STARS.length < 26) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 60);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 26) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.25 + r() * 0.5 });
  }
}
/* low snow clouds, tileable (period W): puffy banks, cold on top, lit from below by the city */
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 50 + r() * 90, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 20, ry = 3.5 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.15 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col, def.a);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
const CLOUD_DEFS = [
  { seed: 11, y: -8, h: 58, banks: 7, k: 1, a: 255, pal: { top: '#25273e', body: '#1c1f34', core: '#181a2e', bot: '#2e2b42', bot2: '#25253a' } },
  { seed: 29, y: 26, h: 60, banks: 6, k: 2, a: 245, pal: { top: '#32334e', body: '#26283e', core: '#202236', bot: '#4a4058', bot2: '#36324a' } },
  { seed: 43, y: 70, h: 58, banks: 6, k: 3, a: 235, pal: { top: '#403e58', body: '#302e46', core: '#2a283e', bot: '#6a5462', bot2: '#4c4054' } },
];

/* ================= Windows that switch on and off; Advent stars and candlesticks ================= */
// Swedish windows in December: a paper star hung in the middle, an electric candlestick of seven lights on the sill.
// Both glow whether the room behind is lit or not. deco: 'star' | 'candle' | null
const WINS = [];
const LIT = [['#ffe2a0', '#f2b866'], ['#ffd690', '#e8a050'], ['#fff0c8', '#f0cc88'], ['#f6c07a', '#d08440'], ['#dfe4f2', '#aab4d0']];
function addWin(r, x, y, w, h, opt = {}) {
  const v = r(), on = opt.pOn ?? 0.4, mode = opt.mode || (v < on ? 'on' : v < on + 0.32 ? 'tog' : 'off'), lp = LIT[Math.floor(r() * r() * 5)];
  let deco = opt.deco ?? null;
  if (deco === null && w >= 3 && h >= 3) { const d = r(); deco = d < 0.13 ? 'star' : d < 0.22 && w >= 5 ? 'candle' : null; }
  WINS.push({ x, y, w, h, mode, k: 2 + Math.floor(r() * 3), ph: r(), duty: 0.35 + r() * 0.35, c1: lp[0], c2: lp[1], deco: deco || null, id: WINS.length });
}
const STAR5 = ['..1..', '11111', '.111.', '.1.1.'], STAR3 = ['.1.', '111', '.1.'];
/** the decoration in window w (per frame, over the glass) */
function drawDeco(w) {
  if (w.deco === 'star') {
    if (w.w < 4 || w.h < 5) { ctx.fillStyle = '#fff6e0'; ctx.fillRect(w.x + (w.w >> 1), w.y + 1, 1, 1); return; }   // far off: a point of light
    const S = w.w >= 5 && w.h >= 5 ? STAR5 : STAR3, sx = w.x + ((w.w - S[0].length) >> 1), sy = w.y + (w.h >= 6 ? 1 : 0);
    ctx.fillStyle = '#fff2d2';
    S.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') ctx.fillRect(sx + i, sy + j, 1, 1); });
    if (S === STAR5) { ctx.fillStyle = '#ffd890'; ctx.fillRect(sx + 2, sy + 1, 1, 1); ctx.fillStyle = '#8a6a50'; ctx.fillRect(sx + 2, w.y, 1, sy - w.y); }
  } else if (w.deco === 'candle') {                                                     // seven lights stepping up to the middle, on a wooden stand
    const n = w.w >= 7 ? 7 : 5, half = n >> 1, x0 = w.x + ((w.w - n) >> 1), base = w.y + w.h - 1;
    ctx.fillStyle = '#4a2c1c'; ctx.fillRect(x0, base, n, 1);
    for (let i = 0; i < n; i++) {
      const k = half - Math.abs(i - half);
      ctx.fillStyle = '#5a3a26'; ctx.fillRect(x0 + i, base - k, 1, 1);
      ctx.fillStyle = '#fff4cc'; ctx.fillRect(x0 + i, base - 1 - k, 1, 1);
    }
  }
}

/* ================= Far west: the open water to Mälaren, Långholmen, Västerbron, the houses of Norr Mälarstrand ================= */
const VB = { x0: 112, x1: 206, deck: 151, arches: [[118, 160], [160, 202]] };   // Västerbron: the deck's top row, its two arches
const VB_LAMPS = [];
const NM = { x0: 186, x1: 240 };                    // Norr Mälarstrand's houses, receding west from Stadshuset
function paintFarWest(p, r) {
  // the far shore at the end of the water: Lilla Essingen's lights
  for (let x = 112; x < 200; x++) {
    const top = 157 + Math.round(vnoise(x / 9, 1, 41) * 2);
    for (let y = top; y < WL; y++) p.set(x, y, pick(['#1a1c30', '#22233a', '#2c2a42'], 0.3 + (y - top) * 0.12, x, y));
    if (hash(x, 3, 42) < 0.12) p.set(x, top + 1, hash(x, 4, 42) < 0.5 ? '#c8a070' : '#8a90b0');
  }
  // Västerbron: a steel deck on two shallow arches, spandrel posts, its lamps
  for (let x = VB.x0; x <= VB.x1; x++) {
    p.set(x, VB.deck, '#6a6a88'); p.set(x, VB.deck + 1, '#3a3a56'); p.set(x, VB.deck + 2, '#1c1c2e');
    if (x % 5 === 0) { p.set(x, VB.deck - 1, '#3a3a52'); VB_LAMPS.push(x); }
  }
  for (const [a, b] of VB.arches) {
    const mid = (a + b) / 2, half = (b - a) / 2;
    for (let x = a; x <= b; x++) {
      const u = (x - mid) / half, top = Math.round(WL - 1 - 6.5 * Math.sqrt(Math.max(0, 1 - u * u)));
      p.set(x, top, '#64648a'); p.set(x, top + 1, '#383858');
      if (x % 3 === 0) for (let y = VB.deck + 3; y < top; y++) p.set(x, y, '#2c2c46');   // spandrel posts
    }
    for (let y = WL - 3; y < WL; y++) { p.set(a, y, '#30304a'); p.set(a + 1, y, '#24243a'); }   // the piers' feet
  }
  // Långholmen: low bare woods and snowy rocks in front of the bridge's south end
  for (let x = 96; x < 146; x++) {
    const top = 147 + Math.round(4 * vnoise(x / 5, 2, 43) + 3 * smooth(118, 146, x) + 2 * smooth(110, 96, x));
    for (let y = top; y < WL; y++) {
      const v = 0.25 + 0.3 * vnoise(x / 2, y / 2, 44) + (y > WL - 3 ? -0.15 : 0);
      p.set(x, y, y === top || (bayer(x, y) < 0.3 && y < top + 3) ? '#5a6078' : pick(['#0e1020', '#151628', '#1c1d32'], v, x, y));
    }
    if (hash(x, 5, 45) < 0.18) for (let y = top - 3; y < top; y++) p.set(x, y, '#1a1a2c');   // twigs against the far water
  }
  for (const [x, y] of [[128, 155], [134, 156], [139, 154]]) { p.set(x, y, '#e8c088'); p.set(x + 1, y, '#c89058'); }   // the old prison's windows
  // Norr Mälarstrand: apartment blocks of the 1920s and 30s along the quay, brick and plaster, some under pitched roofs,
  // the promenade's bare trees in front; lower and hazier the further west they stand
  let x = NM.x0, k = 0;
  const cols = ['#6a4e44', '#5a4a52', '#76604c', '#5e5260', '#6c4a40', '#64584e'];
  while (x < NM.x1) {
    const u = (x - NM.x0) / (NM.x1 - NM.x0), w = Math.min(NM.x1 - x, 6 + Math.floor(r() * 6 + u * 3));
    const top = Math.round(lerp(148, 136, u) + [0, -3, 1, -2, 2, -1, 1][k % 7]), pitched = k % 3 !== 1, col = cols[k % cols.length];
    for (let yy = top; yy < WL - 2; yy++) for (let i = x; i < x + w; i++) {
      let c = mixHex(col, '#1c2036', 0.42 - u * 0.18 + 0.2 * (1 - smooth(top, WL - 2, yy)));
      if (i === x) c = mixHex(col, '#0c0e1c', 0.62);
      else if (yy === top + 1 && !pitched) c = mixHex(col, '#0c0e1c', 0.5);
      p.set(i, yy, c);
    }
    if (pitched) for (let j = 1; j <= 3; j++) for (let i = x + j - 1; i <= x + w - j; i++) p.set(i, top - j + 1, j === 3 || bayer(i, j) < 0.6 ? '#9ca4be' : '#747c9a');
    else for (let i = x; i < x + w; i++) p.set(i, top, '#8a90aa');
    if (k % 4 === 2) { p.set(x + 2, top - 3, '#3a2c30'); p.set(x + 2, top - 4, '#3a2c30'); p.set(x + 2, top - 5, '#9ca4be'); }   // a chimney
    for (let wy = top + 3; wy < WL - 5; wy += 3) for (let wx = x + 2; wx < x + w - 1; wx += 2) {
      const v = r();
      if (v < 0.36) addWin(r, wx, wy, 1, 1, { deco: false });
      else if (v < 0.4) p.set(wx, wy, '#fff0c8');                                         // a star in the window: a point of light from here
    }
    x += w; k++;
  }
  for (let i = NM.x0; i < NM.x1; i++) {                                                   // the quay: its wall and lamps
    p.set(i, WL - 2, '#3a3c52'); p.set(i, WL - 1, '#202234');
    if (i % 9 === 4) { p.set(i, WL - 6, '#fff0c0'); for (let y = WL - 5; y < WL - 2; y++) p.set(i, y, '#1a1c2c'); }
  }
  for (let tx = NM.x0 + 3; tx < NM.x1 - 2; tx += 6 + Math.floor(r() * 3)) {              // the promenade's bare lime trees
    for (let y = WL - 6; y < WL - 2; y++) p.set(tx, y, '#141422');
    for (let j = 0; j < 7; j++) { const a = -Math.PI / 2 + (r() - 0.5) * 2.4, len = 2 + r() * 3; p.line(tx, WL - 6, tx + Math.cos(a) * len, WL - 6 + Math.sin(a) * len, '#181828'); }
    p.set(tx - 1, WL - 9, '#6a7292'); p.set(tx + 1, WL - 8, '#6a7292');
  }
}

/* ================= Södermalm: houses on Söder Mälarstrand, the cliff, Mariaberget and Monteliusvägen on top ================= */
const SOD_X1 = 124;
const QUAY_HOUSES = [                               // along the water, west to the right: x0, x1, roof, colour
  [-2, 30, 139, '#a8885c'], [30, 54, 142, '#76808e'], [54, 76, 141, '#a86c58'], [76, 94, 145, '#b8a46a'], [94, 120, 148, '#7e4a3a'],
];
const TOP_HOUSES = [                                // on the cliff: x0, x1, roof (eaves for pitched roofs), colour, kind
  [-2, 19, 100, '#7a6e62', 'pitch'], [19, 39, 108, '#8a3a30', 'wood'], [39, 62, 103, '#9a8a6a', 'pitch'], [62, 106, BB_ROOF, '#5e5a6c', 'block'],
  [106, 126, 121, '#6a5a52', 'pitch'],
];
const cliffTop = x => Math.round(lerp(117, 137, clamp(x / 116, 0, 1)) + 2 * Math.sin(x * 0.21));
const SOD_LAMPS = [];                               // Monteliusvägen's lamps along the cliff's edge
function facade(p, x0, x1, top, base, col, lightBelow) {
  for (let y = top; y < base; y++) for (let x = Math.max(0, x0); x < x1; x++) {
    const v = 0.3 + (lightBelow ? 0.35 * smooth(base - 30, base, y) : 0.1) + (vnoise(x / 3, y / 2, 7) - 0.5) * 0.08 - (x === x0 ? 0.15 : 0);
    p.set(x, y, pick([mixHex(col, '#0a0c1c', 0.75), mixHex(col, '#0a0c1c', 0.6), mixHex(col, '#141830', 0.45), mixHex(col, '#2a2030', 0.3), mixHex(col, '#ffc890', 0.12)], v, x, y, 0.6));
  }
}
function paintSodermalm(p, r) {
  // the cliff: dark granite with snow on its ledges, a few bare trees, the steps up from the quay
  for (let x = 0; x < SOD_X1; x++) {
    const top = cliffTop(x), bot = WL - 14;
    for (let y = top; y < bot; y++) {
      const n = vnoise(x / 4, y / 3, 51), ledge = vnoise(x / 5, y / 1.3, 52) > 0.58 && n < 0.62;
      p.set(x, y, ledge ? (bayer(x, y) < 0.6 ? '#9aa2c0' : '#6a7292') : pick(['#12131f', '#1a1a2a', '#242334', '#2e2b3a', '#3a3442'], 0.15 + n * 0.75, x, y));
    }
    for (let y = top - 1; y <= top; y++) p.set(x, y, '#9aa2c0');                         // snow along the cliff's edge
  }
  for (const tx of [12, 47, 71, 101]) {                                                  // bare trees in the cliff
    const ty = cliffTop(tx) + 6;
    for (let k = 0; k < 9; k++) p.set(tx + Math.round(Math.sin(k * 0.7) * 0.6), ty + 8 - k, '#0a0a14');
    for (let k = 0; k < 14; k++) { const a = -Math.PI / 2 + (r() - 0.5) * 2.2, len = 3 + r() * 5; p.line(tx, ty + 1, tx + Math.cos(a) * len, ty + 1 + Math.sin(a) * len, '#0c0c18'); }
  }
  for (let k = 0; k < 18; k++) { const sx = 60 + (k % 6) * 2 + (Math.floor(k / 6) % 2 ? 6 : 0), sy = WL - 15 - k; p.set(sx, sy, '#5a6080'); }   // a stair
  // the houses on the cliff top: an apartment block, a red wooden house from the 1700s, the block with the sign, another
  for (const [x0, x1, roof, col, kind] of TOP_HOUSES) {
    const base = Math.max(cliffTop(x0), cliffTop(x1 - 1)) + 2;
    if (kind === 'wood') {
      const eave = roof + 6;
      facade(p, x0, x1, eave, base, col, false);
      for (let y = roof; y < eave; y++) {                                                // a snowy roof, the gable end towards us
        const inset = Math.round((eave - y) * 1.2);
        for (let x = x0 + inset - 1; x < x1 - inset + 1; x++) p.set(x, y, bayer(x, y) < 0.7 ? '#b4bcd4' : '#8a92ae');
      }
      for (let y = eave; y < base; y++) { p.set(x0, y, '#e8e0d0'); p.set(x1 - 1, y, '#c8c0b4'); }   // white corner boards
      for (const wx of [x0 + 4, x0 + 12]) addWin(r, wx, eave + 4, 4, 5, { mode: 'on', deco: wx === x0 + 4 ? 'candle' : 'star' });
      continue;
    }
    facade(p, x0, x1, roof, base, col, false);
    if (kind === 'pitch') {                                                              // an old house: a tin roof under snow, a chimney
      for (let y = roof - 6; y < roof; y++) { const inset = Math.round((roof - y) * 0.9); for (let x = Math.max(0, x0 + inset - 1); x < x1 - inset + 1; x++) p.set(x, y, y === roof - 6 || bayer(x, y) < 0.75 ? '#b4bcd4' : '#7a82a0'); }
      const cx = x0 + ((x1 - x0) >> 1) + 3;
      for (let y = roof - 10; y < roof - 4; y++) { p.set(cx, y, '#4a3036'); p.set(cx + 1, y, '#3a242a'); }
      p.set(cx, roof - 11, '#c4ccdf'); p.set(cx + 1, roof - 11, '#c4ccdf');
      for (let x = Math.max(0, x0); x < x1; x++) p.set(x, roof, mixHex(col, '#0a0c1c', 0.7));
    } else for (let x = Math.max(0, x0); x < x1; x++) { p.set(x, roof, '#aab2cc'); p.set(x, roof + 1, '#5c6280'); }   // snow on the parapet
    for (let wy = roof + 4; wy < base - 4; wy += 6) for (let wx = x0 + 3; wx < x1 - 3; wx += kind === 'block' ? 6 : 5) if (r() < 0.86) addWin(r, wx, wy, kind === 'block' ? 3 : 2, kind === 'block' ? 4 : 3, { pOn: 0.3 });
  }
  for (let x = 2; x < 100; x += 13) { SOD_LAMPS.push({ x, y: cliffTop(x) - 4 }); for (let y = cliffTop(x) - 3; y < cliffTop(x); y++) p.set(x, y, '#141420'); }
  for (let x = 0; x < 104; x++) if (x % 3 === 0) p.set(x, cliffTop(x) - 2, '#2a2a3a');      // Monteliusvägen's railing
  // the houses on the quay: plastered fronts lit from the street, shop windows on the ground floor; a brewery at the west end
  for (const [x0, x1, roof, col] of QUAY_HOUSES) {
    const base = WL - 3, brew = col === '#7e4a3a';
    facade(p, x0, x1, roof, base, col, true);
    for (let x = Math.max(0, x0); x < x1; x++) { p.set(x, roof, '#b8c0d8'); p.set(x, roof + 1, '#8088a4'); p.set(x, roof + 2, mixHex(col, '#0a0c1c', 0.7)); }
    if (brew) {
      for (let y = 124; y < roof; y++) for (let x = 112; x < 115; x++) p.set(x, y, x === 112 ? '#9a5a40' : '#6a3628');   // the chimney
      p.set(112, 123, '#aab2cc'); p.set(113, 123, '#aab2cc'); p.set(114, 123, '#8a92ae');
      for (let wx = x0 + 2; wx < x1 - 2; wx += 4) addWin(r, wx, roof + 4, 2, 3, { deco: false, pOn: 0.3 });
      continue;
    }
    for (let wy = roof + 4; wy < base - 9; wy += 5) for (let wx = x0 + 3; wx < x1 - 3; wx += 5) addWin(r, wx, wy, 2, 3, { pOn: 0.36 });
    const sw = Math.min(9, x1 - x0 - 6);
    addWin(r, x0 + 3, base - 7, sw, 6, { mode: 'on', deco: sw >= 7 ? 'candle' : 'star' });   // a lit shop window
  }
  for (let x = 0; x < SOD_X1; x++) { p.set(x, WL - 3, '#565a72'); p.set(x, WL - 2, '#3a3c50'); p.set(x, WL - 1, '#22233a'); }   // the quay's edge
}

/* ================= Stadshuset: dark red brick floodlit warm from the quay, copper roofs under a dusting of snow ================= */
// The ratios it is drawn to: the tower is 106 m to the crowns' top, about 20 m square; the gallery round the top of the
// brick shaft is 73 m above the water, so the shaft is 0.69 of the height and the copper lantern, the cupola, the spire
// and the crowns the other 0.31. The wings' eaves stand at about a quarter of the tower's height. Each crown is 2.2 m
// wide and 1.1 m high (3 x 2 px here: 0.77 m a pixel).
const BRICK = ['#1e080b', '#331012', '#4a1715', '#621f18', '#7b2a1c', '#953720', '#ad4626', '#c3572e', '#d76c3a', '#e8864c', '#f4a468'];
const COPPER = ['#0e1a1a', '#152624', '#1e3530', '#28463c', '#345848', '#466c58', '#5e846c', '#7ea086'];
const GOLD = '#f0c454', GOLDH = '#fff0b0', GOLDD = '#a0742a';
const ROOF_CU = ['#132624', '#1b3230', '#25423c', '#305246'], ROOF_SNOW = ['#59617c', '#737b98', '#959db8', '#b4bcd2'];
const STONE = ['#3a3644', '#58505a', '#7e6c6a', '#a88a7a', '#c8a88e'];
const wingOf = w => (w === 'S' ? SWING : EWING);
/** where a wing's column stands: u 0 at the tower .. 1 at the far end, s the scale there (the far end is further off) */
function wingAt(w, x) {
  const W0 = wingOf(w), u = w === 'S' ? (W0.x1 - x) / (W0.x1 - W0.x0) : (x - W0.x0) / (W0.x1 - W0.x0);
  return { u, s: 1 - PERSP * u };
}
/** the screen column of a point d near-pixels along a wing's front from the tower (perspective) */
function wingX(w, d) {
  const W0 = wingOf(w), L = W0.x1 - W0.x0, k = d / (1 + d * PERSP / L);
  return w === 'S' ? W0.x1 - k : W0.x0 + k;
}
const yAt = (s, h) => Math.round(WL - h * s);
function brickTex(x, y) {
  const row = y >> 1, off = (row & 1) * 2;
  return ((y & 1) ? -0.045 : 0) + (hash((x + off) >> 2, row, 61) - 0.5) * 0.09;
}
const POOLS = [256, 282, 350, 378];                 // floodlights along the quay: warm pools up the walls
function wingLight(x, h, south) {
  let v = 0.7 - 0.42 * h / EAVE - (south ? 0.1 : 0);
  for (const q of POOLS) v += 0.13 * Math.exp(-(((x - q) / 9) ** 2) - h / 16);
  return v;
}
const HALL_LIGHTS = [];                             // lamps under the arcade's vaults (glints per frame)
function paintWing(p, r, w) {
  const W0 = wingOf(w), south = w === 'S', L = W0.x1 - W0.x0;
  for (let x = W0.x0; x <= W0.x1; x++) {
    const { u, s } = wingAt(w, x);
    const ridge = RIDGE - Math.max(0, (u - 0.9) / 0.1) * (RIDGE - EAVE - 3);           // hipped at the far end
    const yTop = yAt(s, ridge);
    for (let y = yTop; y < WL; y++) {
      const h = (WL - y) / s;
      let col;
      if (h > EAVE + 0.6) {                                                             // the copper roof, snow lying on it
        const f = (h - EAVE) / Math.max(1, ridge - EAVE), n = 0.7 * vnoise(x / 7, y / 2.2, south ? 71 : 72) + 0.3 * vnoise(x / 2, y, 74);
        col = n + 0.32 * (1 - f) > 0.68 ? pick(ROOF_SNOW, 0.35 + n * 0.6 - f * 0.25, x, y, 0.4) : pick(ROOF_CU, 0.25 + f * 0.5 + n * 0.25, x, y, 0.4);
        if (y === yTop) col = '#c4ccdf';                                                 // snow along the ridge
      } else if (h > EAVE - 1.6) col = h > EAVE - 0.5 ? pick(STONE, 0.55 + wingLight(x, h, south) * 0.3, x, y) : '#2a0e10';   // cornice, its shadow
      else if (h < 2) col = pick(STONE, 0.35 + wingLight(x, 0, south) * 0.5, x, y);    // the granite base
      else if (south ? h > 12.4 && h < 13.6 : h > 10.4 && h < 11.6) col = pick(STONE, 0.45 + wingLight(x, h, south) * 0.45, x, y);   // a string course
      else col = pick(BRICK, wingLight(x, h, south) + brickTex(x, y), x, y, 0.7);
      if (x === W0.x0 && !south || x === W0.x0 && south && u > 0.98) col = mixHex(col, '#0a0408', 0.4);
      p.set(x, y, col);
    }
  }
  const put = (d, h0, h1, ww, fn) => {                                                  // a feature d along the front, rows h0..h1
    const xc = wingX(w, d), { s } = wingAt(w, Math.round(xc)), x0 = Math.round(xc - ww * s / 2);
    for (let y = yAt(s, h1); y <= yAt(s, h0); y++) for (let x = x0; x < x0 + Math.max(1, Math.round(ww * s)); x++) fn(x, y, (WL - y) / s, s, xc);
    return { x0, y0: yAt(s, h1), y1: yAt(s, h0), w: Math.max(1, Math.round(ww * s)), s };
  };
  if (south) {
    for (let d = 3.5; d < L - 3; d += 6.2) {                                            // the arcade along the water: tall round arches
      put(d, 2, 11.8, 4.6, (x, y, h, s, xc) => {
        const k = h > 8.6 ? Math.sqrt(Math.max(0, 1 - ((h - 8.6) / 3.2) ** 2)) : 1;
        if (Math.abs(x + 0.5 - xc) > 2.3 * s * k + 0.2) return;
        p.set(x, y, pick(['#1a0a0e', '#2a1012', '#46201a', '#6a3622', '#8a4a2a'], 0.12 + 0.55 * smooth(2, 10, h) * (1 - Math.abs(x + 0.5 - xc) / 3), x, y));
      });
      HALL_LIGHTS.push({ x: Math.round(wingX(w, d)), y: yAt(wingAt(w, Math.round(wingX(w, d))).s, 10) });
    }
    for (let d = 3; d < L - 2; d += 4.6) {                                              // the gallery's tall windows, and those over them
      const a = put(d, 15, 22, 2, () => {});
      for (let y = a.y0; y <= a.y1; y++) for (let x = a.x0; x < a.x0 + a.w; x++) p.set(x, y, '#1a0c10');
      addWin(r, a.x0, a.y0, a.w, a.y1 - a.y0 + 1, { deco: false, mode: r() < 0.5 ? 'on' : undefined });
      const b = put(d + 2.3, 25, 28, 1, () => {});
      for (let y = b.y0; y <= b.y1; y++) p.set(b.x0, y, '#1a0c10');
      if (r() < 0.4) addWin(r, b.x0, b.y0, 1, b.y1 - b.y0 + 1, { deco: false });
    }
  } else {
    for (let d = 3; d < L - 2; d += 5.2) {                                              // the ground floor, then the upper rows
      const a = put(d, 4, 9, 1, () => {});
      for (let y = a.y0; y <= a.y1; y++) p.set(a.x0, y, '#1a0c10');
      if (r() < 0.5) addWin(r, a.x0, a.y0, 1, a.y1 - a.y0 + 1, { deco: false });
      const hall = d > 12 && d < 42;                                                    // the council chamber's lofty windows
      const b = put(d, 14, hall ? 28 : 20, 2, () => {});
      for (let y = b.y0; y <= b.y1; y++) for (let x = b.x0; x < b.x0 + b.w; x++) p.set(x, y, '#1a0c10');
      addWin(r, b.x0, b.y0, b.w, b.y1 - b.y0 + 1, { deco: false, mode: hall ? 'on' : undefined });
      if (hall) { for (let y = b.y0; y <= b.y1; y += 4) for (let x = b.x0; x < b.x0 + b.w; x++) p.set(x, y, '#3a1a14'); }   // transoms (over the glass: drawn again per frame)
      else { const c = put(d, 23, 27, 1, () => {}); for (let y = c.y0; y <= c.y1; y++) p.set(c.x0, y, '#1a0c10'); if (r() < 0.4) addWin(r, c.x0, c.y0, 1, c.y1 - c.y0 + 1, { deco: false }); }
    }
  }
  for (const d of south ? [21, 49] : [31, 56]) {                                        // chimneys, snow on their caps
    const xc = Math.round(wingX(w, d)), { s } = wingAt(w, xc), y0 = yAt(s, RIDGE - 2);
    for (let y = y0 - 5; y < y0 + 2; y++) for (let x = xc; x < xc + 3; x++) p.set(x, y, y === y0 - 5 ? '#c4ccdf' : x === xc ? '#7a3424' : '#4a1c18');
  }
  const fx = Math.round(wingX(w, south ? 34 : 22)), fs = wingAt(w, fx).s;               // a gilded finial on the ridge
  for (let k = 1; k <= 4; k++) p.set(fx, yAt(fs, RIDGE) - k, k === 4 ? GOLDH : GOLD);
}
/** the higher block behind the south wing (over the Blue Hall and the Golden Hall), its roof showing over the ridge */
function paintInner(p) {
  for (let x = 252; x <= 294; x++) {
    const top = 113 + (x > 290 ? (x - 290) : 0) + (x < 256 ? 256 - x : 0);
    for (let y = top; y < 126; y++) {
      const n = 0.7 * vnoise(x / 7, y / 2.2, 73) + 0.3 * vnoise(x / 2, y, 75);
      p.set(x, y, y === top ? '#b4bcd2' : n + 0.03 * (y - top) > 0.62 ? pick(ROOF_SNOW, 0.2 + n * 0.5, x, y, 0.4) : pick(ROOF_CU, 0.15 + (y - top) * 0.05, x, y, 0.4));
    }
  }
  for (let k = 1; k <= 6; k++) p.set(273, 112 - k, k > 4 ? GOLDH : k > 2 ? GOLD : GOLDD);   // its gilded spirelet
}
/** the tower: the brick shaft seen across its south-east corner, the gallery, the open lantern, the cupola, the crowns */
function towerLight(x, y) {
  let v = 0.4 + 0.36 * Math.exp(-(WL - y) / 42) + 0.2 * Math.exp(-(((y - 82) / 13) ** 2));
  if (x >= T.se) v += 0.1 - 0.12 * smooth(T.ne - 4, T.ne, x); else v -= 0.1 + 0.1 * smooth(T.sw + 3, T.sw, x);
  if (x === T.se) v += 0.16;                                                            // the corner catches the light
  return v;
}
function paintTower(p, r) {
  for (let y = T.shaft; y < WL; y++) for (let x = T.sw; x <= T.ne; x++) {
    let col = y >= WL - 5 ? pick(STONE, 0.5 + (x >= T.se ? 0.25 : 0) + (y === WL - 5 ? 0.2 : 0), x, y) : pick(BRICK, towerLight(x, y) + brickTex(x, y), x, y, 0.7);
    if (y === 95 || y === 141) col = pick(STONE, 0.4 + (x >= T.se ? 0.35 : 0.1), x, y);   // string courses
    p.set(x, y, col);
  }
  // the niche at the foot of the south face: Birger Jarl's cenotaph, a gilded figure under its canopy
  for (let y = 144; y < WL - 5; y++) for (let x = 310; x <= 315; x++) {
    const k = y < 147 ? Math.sqrt(Math.max(0, 1 - ((147 - y) / 3.2) ** 2)) * 3 : 3;
    if (Math.abs(x - 312.5) <= k) p.set(x, y, y > 150 ? '#3a1c14' : '#1a0a0c');
  }
  p.set(312, 152, GOLD); p.set(313, 152, GOLDH); p.set(312, 153, GOLDD); p.set(313, 153, GOLD);
  for (let x = 311; x <= 314; x++) p.set(x, 154, '#6a5a50');
  // the belfry's tall openings near the top: three on the east face, two on the south face, their reveals lit
  const slot = (x0, y0, y1, lit) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x < x0 + 2; x++) if (y > y0 || x === x0) p.set(x, y, y > y1 - 2 ? '#2a1010' : '#120608');
    for (let y = y0 + 1; y <= y1; y++) p.set(x0 - 1, y, lit ? '#e8864c' : '#953720');
    for (let x = x0 - 1; x <= x0 + 2; x++) p.set(x, y1 + 1, lit ? '#d76c3a' : '#7b2a1c');
  };
  for (const x0 of [321, 326, 331]) slot(x0, 73, 89, true);
  for (const x0 of [310, 314]) slot(x0, 74, 89, false);
  for (let x = 323; x <= 332; x++) { p.set(x, 93, '#c8a88e'); p.set(x, 92, x % 2 ? '#2a1214' : '#5a2a1c'); p.set(x, 91, '#3a1a16'); }   // the carillon's balcony
  for (const [x, y] of [[327, 104], [327, 120], [327, 136], [312, 110], [312, 128]]) {   // the stairs' windows
    for (let k = 0; k < 4; k++) p.set(x, y + k, '#160a0c');
    addWin(r, x, y, 1, 4, { deco: false, mode: 'tog' });
  }
  for (let x = T.sw - 1; x <= T.ne + 1; x++) {                                         // corbels and the stone cornice
    p.set(x, 67, x % 2 ? pick(BRICK, towerLight(x, 70) + 0.1, x, 67) : '#3a1412');
    p.set(x, 66, pick(STONE, 0.6 + (x >= T.se ? 0.3 : 0), x, 66));
  }
  // the lantern: square, set back on the gallery, green copper, gilded bands, tall open arches lit from inside
  for (let y = LAN.top; y <= 65; y++) for (let x = LAN.sw; x <= LAN.ne; x++) {
    const east = x >= LAN.se;
    let v = 0.3 + (east ? 0.16 : 0) + 0.16 * smooth(LAN.top, 65, y);
    if (x === LAN.se) v += 0.25; else if (x === LAN.sw || x === LAN.ne) v -= 0.18;
    let col = pick(COPPER, v + (hash(x, y, 81) - 0.5) * 0.08, x, y, 0.6);
    if (y === LAN.top || y === LAN.bot) col = x === LAN.se || (east && y === LAN.top) ? GOLDH : GOLD;
    p.set(x, y, col);
  }
  const arch = (x0, w, y0, y1) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x < x0 + w; x++) if (y > y0 || w < 3 || x === x0 + 1) p.set(x, y, y < y0 + 4 ? '#ffe7a8' : y < y0 + 9 ? '#ffcf7c' : '#e89c52');
  };
  arch(321, 3, 48, 59); arch(327, 3, 48, 59); arch(315, 2, 49, 59);
  for (let x = T.sw; x <= T.ne; x++) {                                                 // the gallery's grating
    p.set(x, 62, '#4a2a22');
    if (x % 2 === 0) for (let y = 63; y <= 65; y++) p.set(x, y, '#2a1614');
  }
  for (const [cx, w] of [[T.sw, 2], [T.se - 1, 3], [T.ne - 1, 2]]) {                    // corner turrets, gilded tips
    for (let y = 57; y <= 65; y++) for (let x = cx; x < cx + w; x++) p.set(x, y, pick(COPPER, 0.5 + (x === cx ? -0.15 : 0.15) + (cx === T.se - 1 ? 0.2 : 0), x, y));
    p.set(cx + (w >> 1), 56, GOLD); p.set(cx + (w >> 1), 55, GOLDH);
  }
  for (let x = LAN.sw - 1; x <= LAN.ne + 1; x++) {                                     // the gilded cornice over the lantern
    p.set(x, 44, x >= LAN.se ? GOLDH : GOLD); p.set(x, 43, x >= LAN.se ? GOLD : GOLDD); p.set(x, 42, GOLDD);
  }
  [1, 1, 2, 2, 3, 4, 6, 8].forEach((hw, j) => {                                         // the cupola, rows 34..41: an ogee, gilded ribs
    const y = 34 + j;
    for (let x = T.cx - hw; x <= T.cx + hw; x++) {
      let col = pick(COPPER, 0.3 + (x - T.cx) / 18 + j * 0.05, x, y);
      if (x === T.cx || (hw > 2 && (x === T.cx - hw || x === T.cx + hw))) col = x === T.cx ? GOLD : x < T.cx ? GOLDH : GOLDD;
      p.set(x, y, col);
    }
  });
  for (let y = 28; y <= 33; y++) p.set(T.cx, y, y === 31 ? GOLDH : GOLD);                 // the spire, its orb
  p.set(T.cx - 1, 31, GOLD); p.set(T.cx + 1, 31, GOLDD);
  const crown = (x0, y0) => {                                                             // points, the band, the rim
    p.set(x0, y0, GOLDH); p.set(x0 + 2, y0, GOLDH);
    p.set(x0, y0 + 1, GOLDH); p.set(x0 + 1, y0 + 1, GOLD); p.set(x0 + 2, y0 + 1, GOLD);
    p.set(x0, y0 + 2, GOLD); p.set(x0 + 1, y0 + 2, GOLDD); p.set(x0 + 2, y0 + 2, GOLDD);
  };
  crown(T.cx - 1, T.top); crown(T.cx - 4, T.top + 3); crown(T.cx + 2, T.top + 3);         // three crowns: two below, one above
  for (let y = T.top + 3; y < 28; y++) p.set(T.cx, y, GOLDD);
}
/** the small tower over the east wing: a slim brick shaft, a copper belfry, a gilded dome */
function paintSmallTower(p) {
  const { x0, x1, cx } = SMALL, se = x0 + 4, roof = yAt(wingAt('E', cx).s, RIDGE - 4);
  for (let y = 110; y <= roof; y++) for (let x = x0; x <= x1; x++)
    p.set(x, y, pick(BRICK, 0.44 + (x >= se ? 0.14 : -0.08) + 0.12 * smooth(roof - 8, roof, y) + brickTex(x, y), x, y, 0.7));
  for (let x = x0 - 1; x <= x1 + 1; x++) { p.set(x, 109, pick(STONE, x >= se ? 0.8 : 0.5, x, 109)); p.set(x, 110, '#2a0e10'); }
  for (let y = 102; y <= 108; y++) for (let x = x0 + 1; x <= x1 - 1; x++) p.set(x, y, pick(COPPER, 0.4 + (x >= se ? 0.2 : 0) + (x === se ? 0.2 : 0), x, y));
  for (const ax of [x0 + 2, se + 1, se + 4]) for (let y = 103; y <= 107; y++) if (y > 103 || ax !== x0 + 2) p.set(ax, y, y < 105 ? '#ffd890' : '#e89c52');   // its open belfry
  for (let x = x0; x <= x1; x++) p.set(x, 101, x >= se ? GOLD : GOLDD);
  [1, 2, 3, 4, 4, 5].forEach((hw, j) => { for (let x = cx - hw + 1; x <= cx + hw; x++) p.set(x, 95 + j, x <= cx - hw + 2 ? GOLDH : x >= cx + hw ? GOLDD : GOLD); });   // the gilded dome
  for (let y = SMALL.top; y <= 94; y++) p.set(cx, y, y === SMALL.top + 2 ? GOLDH : GOLD);
  p.set(cx + 1, 94, GOLD);
}

/* ================= Right of the hall: Stadshusbron's quay, Norrmalm across Klara sjö ================= */
const QLAMPS = [414, 432, 450, 468];                // Stadshusbron's lamps
function paintRight(p, r) {
  let x = EWING.x1 + 1;
  while (x < W) {
    const w = 8 + Math.floor(r() * 9), top = 124 + Math.floor(r() * 16), col = ['#3a3446', '#443a48', '#32323e', '#4a3e40'][Math.floor(r() * 4)];
    for (let y = top; y < WL - 4; y++) for (let i = x; i < Math.min(W, x + w); i++)
      p.set(i, y, y === top ? '#a4acc6' : y === top + 1 ? '#5a6080' : i === x ? mixHex(col, '#0a0c1c', 0.6) : pick([mixHex(col, '#0a0c1c', 0.55), mixHex(col, '#0a0c1c', 0.35), mixHex(col, '#ffb070', 0.08)], 0.3 + smooth(top + 10, WL - 4, y) * 0.6, i, y));
    for (let wy = top + 4; wy < WL - 8; wy += 4) for (let wx = x + 2; wx < Math.min(W, x + w) - 1; wx += 3) if (r() < 0.55) addWin(r, wx, wy, 2, 2, { deco: r() < 0.2 ? 'star' : false });
    x += w;
  }
  for (let i = EWING.x1 - 2; i < W; i++) {                                              // the quay where the steamboats lie
    p.set(i, WL - 4, '#8a92ae'); p.set(i, WL - 3, '#5a5c70'); p.set(i, WL - 2, '#3a3a4c'); p.set(i, WL - 1, '#22222e');
  }
  for (const lx of QLAMPS) { for (let y = WL - 13; y < WL - 4; y++) p.set(lx, y, '#16161f'); p.set(lx - 1, WL - 14, '#2a2a36'); p.set(lx + 1, WL - 14, '#2a2a36'); }
}

/* ================= Boats ================= */
// a white archipelago steamboat, bow to the left: varnished deckhouse, a tall funnel, a mast forward, lit windows.
// Its Christmas lights (mast to bow, mast to funnel to stern) are drawn per frame (STEAM_BULBS: offsets in the sprite)
const STM = { w: 60, h: 25 };
const STEAM_BULBS = [];
/** dark: a black hull under the white superstructure (the one that sails, told apart from the one at the quay) */
function renderSteamer(dark = false) {
  const p = pix(STM.w, STM.h), wl = STM.h - 1;
  for (let y = 17; y <= wl; y++) for (let x = 0; x < STM.w; x++) {                         // the hull: raked bow (left), counter stern
    const fromBow = x - Math.max(0, Math.round((wl - y) * 0.2)) - (y > 21 ? (y - 21) * 2 : 0), fromStern = STM.w - 1 - x - (y > 19 ? (y - 19) : 0);
    if (fromBow < 2 + (y - 17) * 0.9 || fromStern < 0) continue;
    p.set(x, y, y === 17 ? '#f8f6f0' : y >= wl - 1 ? (dark ? '#6a2424' : '#1c1e2c') : y === 18 ? (dark ? '#3a3e52' : '#a8b0c4') : dark ? pick(['#101220', '#181a2a', '#262838'], 0.4 + 0.3 * Math.sin(x * 0.2), x, y) : pick(['#9ca2b8', '#c4c8d4', '#e4e4e8'], 0.4 + 0.3 * Math.sin(x * 0.2), x, y));
  }
  for (let x = 12; x < 52; x++) {                                                         // the deckhouse, its windows
    for (let y = 12; y <= 16; y++) p.set(x, y, y === 12 ? '#f4f2ea' : x === 12 ? '#9a6a40' : '#7a4e2e');
    if (x % 4 === 1 && x < 50) { p.set(x, 14, '#ffd88a'); p.set(x + 1, 14, '#ffe6a8'); p.set(x, 15, '#f0b060'); p.set(x + 1, 15, '#f0b060'); }
  }
  for (let x = 18; x < 40; x++) for (let y = 8; y <= 11; y++) p.set(x, y, y === 8 ? '#f4f2ea' : (x % 4 === 2 && y === 10) ? '#ffe6a8' : '#e2e0d8');   // the upper deck saloon
  for (let x = 18; x < 22; x++) for (let y = 5; y <= 7; y++) p.set(x, y, y === 5 ? '#f4f2ea' : x === 19 || x === 20 ? '#ffe0a0' : '#d8d6ce');   // the wheelhouse
  for (let y = 0; y <= 11; y++) for (let x = 30; x <= 32; x++) p.set(x, y, y < 2 ? '#141418' : y < 3 ? '#e8c040' : y < 5 ? '#141418' : x === 30 ? '#3a3a44' : '#1c1c24');   // the funnel
  for (let y = 0; y <= 12; y++) p.set(9, y, '#5a4a3a');                                     // the mast
  p.set(9, 0, '#fff8e8');
  if (dark) return p.done();
  for (let k = 0; k <= 8; k++) STEAM_BULBS.push([Math.round(lerp(9, 0, k / 8)), Math.round(lerp(1, 16, k / 8))]);
  for (let k = 1; k <= 6; k++) STEAM_BULBS.push([Math.round(lerp(9, 31, k / 6)), Math.round(lerp(1, 0, k / 6)) + (k === 3 ? 1 : 0)]);
  for (let k = 1; k <= 9; k++) STEAM_BULBS.push([Math.round(lerp(32, 59, k / 9)), Math.round(lerp(1, 16, k / 9))]);
  return p.done();
}
// the commuter boat across Riddarfjärden, bow to the right: white hull, a blue band, the lit cabin, a white masthead light
const FRY = { w: 42, h: 15 };
function renderFerry(crew = true) {
  const p = pix(FRY.w, FRY.h), wl = FRY.h - 1;
  for (let y = 9; y <= wl; y++) for (let x = 0; x < FRY.w; x++) {
    if (FRY.w - 1 - x < (y - 9) * 1.6 || x < (y > 12 ? 1 : 0)) continue;
    p.set(x, y, y === 9 ? '#f4f2ec' : y === 11 ? '#2a4a9a' : y >= wl - 1 ? '#141826' : '#d8dae2');
  }
  for (let x = 6; x < 34; x++) for (let y = 4; y <= 8; y++) {
    let col = y === 4 ? '#e8eaf0' : x === 6 ? '#9aa0b4' : '#c4c8d4';
    if (y >= 5 && y <= 7 && x % 4 !== 2 && x > 7 && x < 33) col = y === 5 ? '#ffe6b0' : '#f0c27a';
    p.set(x, y, col);
  }
  for (let x = 27; x < 33; x++) for (let y = 2; y <= 4; y++) p.set(x, y, y === 2 ? '#e8eaf0' : '#9ab8d8');   // the wheelhouse
  if (crew) { p.set(30, 3, '#2a1c18'); p.set(30, 4, '#e8b890'); }
  for (let y = 0; y <= 2; y++) p.set(29, y, '#6a6a78');
  p.set(29, 0, '#ffffff');
  return p.done();
}
const SWAN_KEY = { w: '#eef0f6', s: '#b4bcd2', o: '#e08a3a', k: '#1a1a22' };
const SWAN = ['......ww.', '......wok', '......w..', '.....ws..', '.ww..ws..', 'wwwwwws..', 'wwwwwwss.', '.sssss...'];
const DUCK_KEY = { g: '#2a5a40', b: '#5a4a3a', w: '#c8c0b0', y: '#d8a030', d: '#3a3028' };
const DUCK = ['..gg.', '.ggy.', 'bbbbd', 'wbbbd'];
const GULL_KEY = { w: '#e8ecf4', g: '#9aa4c0', k: '#2a3050', y: '#f0b040' };
const GULL_F = [
  ['k.......k', '.gw...wg.', '..wwwww..', '....wy...'],
  ['.........', 'kgww.wwgk', '..wwwww..', '....wy...'],
  ['.........', '..wwwww..', '.gwwwywg.', 'k.......k'],
];
const GULL_SIT_ROWS = ['..ww.', '.wwwy', 'gwww.', '.gg..', '..k..'], GULL_PECK_ROWS = ['.....', '..ww.', 'gwwwy', '.gg..', '..k..'];

/* ================= Riddarfjärden: open dark water, the far side's mirror broken into slivers ================= */
function renderWater() {
  const h = QY - WL, p = pix(W, h), P = ['#04061a', '#070a1f', '#0a0e25', '#0e132c', '#121934'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++)
    p.set(x, y, pick(P, 0.62 - 0.45 * (y / h) + (vnoise(x / 40, y / 3, 9) - 0.5) * 0.18, x, y, 0.5));
  return p.done();
}
/** the far side as the water mirrors it: a still of everything over the waterline, upside down, squashed, softened and dimmed */
function renderMirror(src) {
  const sx = src.getContext('2d'), data = sx.getImageData(0, 0, W, WL).data, rows = QY - WL, p = pix(W, rows);
  for (let k = 0; k < rows; k++) for (let i = 0; i < W; i++) {
    const sy = WL - 1 - k * 1.9, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -1; dx <= 1; dx++) {
      const yy = Math.round(sy + dy), xx = clamp(i + dx, 0, W - 1);
      if (yy < 0 || yy >= WL) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += data[o]; acc[1] += data[o + 1]; acc[2] += data[o + 2]; n++;
    }
    const f = 0.6 - k * 0.009;
    p.set(i, k, [0, 1, 2].map(c => Math.min(255, Math.floor((acc[c] / n * f + [4, 6, 18][c] * (1 - f)) / 14 + bayer(i, k)) * 14)));
  }
  return p.done();
}
const GLINTS = [];                                  // ripples catching the light, drifting
{
  const r = rng(909);
  for (let i = 0; i < 110; i++) GLINTS.push({ y: WL + 2 + Math.floor(r() * (QY - WL - 3)), x: r(), k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 4)),
    len: 2 + Math.floor(r() * 6), kf: 18 + Math.floor(r() * 40), ph: r(), c: r() < 0.3 ? '#c89878' : r() < 0.75 ? '#5a6488' : '#02040e' });
}
const STREAKS = [
  { x: T.cx - 2, w: 9, col: '#ff9a5a', a: 0.3, seed: 1 }, { x: T.se, w: 4, col: '#ffd090', a: 0.36, seed: 2 },
  ...POOLS.map((x, i) => ({ x, w: 4, col: '#ffb070', a: 0.2, seed: 3 + i })),
  { x: 440, w: 7, col: '#ffd890', a: 0.26, seed: 9 }, ...QLAMPS.map((x, i) => ({ x, w: 2, col: '#ffe0a0', a: 0.24, seed: 10 + i })),
  { x: 20, w: 5, col: '#ffc070', a: 0.18, seed: 15 }, { x: 64, w: 5, col: '#ffc070', a: 0.16, seed: 16 }, { x: 96, w: 3, col: '#ffd090', a: 0.14, seed: 17 },
  { x: 214, w: 3, col: '#ffd090', a: 0.14, seed: 18 }, { x: 156, w: 2, col: '#ffe0a0', a: 0.1, seed: 19 },
];

/* ================= The terrace: its granite edge and the snow on it, the lamps, a bench, Evert Taube ================= */
const SNOWG = ['#2a3250', '#384262', '#465274', '#566488', '#6a789c', '#8290b2', '#9eaac8', '#bcc6de', '#d8e0f0'];
const WARMG = ['#5a5260', '#7e7270', '#a8947e', '#d6ba8c', '#f0d8a6', '#faeac6', '#fff6e2'];
function groundLight(x, y) {
  let s = 0;
  for (const L of LAMPS) s += Math.exp(-(Math.hypot((x - L.x) / 34, (y - 213) / 11) ** 2));
  return Math.min(1, s);
}
// where people walk the snow is trodden grey: along the edge, two paths across, the ways down to the edge
const PATHS = [[[0, 207], [480, 207]], [[0, 229], [150, 231], [300, 228], [480, 230]], [[0, 247], [180, 245], [340, 248], [480, 246]],
  [[112, 272], [150, 236], [194, 209]], [[352, 272], [356, 238], [366, 209]]];
function segDist(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, u = clamp(((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy), 0, 1);
  return Math.hypot(px - ax - u * dx, py - ay - u * dy);
}
function trodden(x, y) {
  let d = 99;
  for (const P of PATHS) for (let i = 0; i + 1 < P.length; i++) d = Math.min(d, segDist(x, y, ...P[i], ...P[i + 1]));
  return Math.max(0, 1 - d / (3 + (y - QY) * 0.09));
}
const BANKS = [{ cx: 26, rx: 74, h: 22 }, { cx: 474, rx: 64, h: 19 }];   // snow shovelled into banks at the corners
const bankTop = x => { let top = H + 1; for (const b of BANKS) { const u = (x - b.cx) / b.rx; if (Math.abs(u) < 1) top = Math.min(top, H - b.h * Math.sqrt(1 - u * u) - 2 * vnoise(x / 6, 1, 25)); } return top; };
function renderTerrace() {
  const p = pix(W, H - QY + 2), Y0 = QY - 2, put = (x, y, c) => p.set(x, y - Y0, c), r = rng(4242);
  for (let x = 0; x < W; x++) {
    const lump = hash(x >> 1, 3, 21) < 0.35 ? 1 : 0, g = groundLight(x, 202), bt = bankTop(x);
    if (lump) put(x, QY - 1, pick(SNOWG, 0.82, x, QY - 1));
    put(x, QY, pick(SNOWG, 0.9 + g * 0.1, x, QY)); put(x, QY + 1, pick(SNOWG, 0.78, x, QY + 1));
    for (let y = QY + 2; y <= QY + 4; y++) {                                            // the kerb's face, granite, snow caught in its joints
      const joint = (x + (y & 1) * 7) % 23 === 0, v = 0.25 + g * 0.45 - (y - QY - 2) * 0.08;
      put(x, y, joint ? '#141626' : hash(x, y, 22) < 0.12 ? '#8a94b4' : g > 0.3 ? pick(WARMG, v, x, y) : pick(['#1c1e30', '#2a2c40', '#3a3c52', '#4c4e64'], v + 0.2, x, y));
    }
    for (let y = QY + 5; y < H; y++) {
      const d = (y - QY - 5) / (H - QY - 5), gg = groundLight(x, y), tr = trodden(x, y);
      let v = 0.5 + 0.16 * d + (vnoise(x / 18, y / 4, 23) - 0.5) * 0.2 + (vnoise(x / 5, y / 2, 24) - 0.5) * 0.06 - 0.24 * tr;
      if (y === QY + 5) v -= 0.25;                                                       // the kerb's shadow
      if (y >= bt) {                                                                     // a bank: lit along its crest, blue in its shadow
        const k = y - bt;
        v = k < 1 ? 0.95 : k < 3 ? 0.82 : 0.66 - k * 0.012 + (vnoise(x / 6, y / 3, 26) - 0.5) * 0.12;
      }
      put(x, y, gg + (bayer(x, y) - 0.5) * 0.24 > 0.5 ? pick(WARMG, 0.25 + gg * 0.7 + (v - 0.5) * 0.4, x, y, 0.5) : pick(SNOWG, v + gg * 0.35, x, y, 0.6));
    }
  }
  for (let k = 0; k < 900; k++) {                                                        // footprints, where people have walked
    const x = Math.floor(r() * W), y = QY + 7 + Math.floor(r() * (H - QY - 9));
    if (trodden(x, y) < 0.35 || y >= bankTop(x) - 1 || r() < 0.3) continue;
    const big = y > 236 ? 1 : 0;
    put(x, y, '#3a4466'); put(x + 1, y, '#3a4466'); if (big) put(x + 2, y, '#465274');
    put(x, y - 1, '#7a88aa');
  }
  return { c: p.done(), x: 0, y: Y0 };
}
const BENCH = { x: 398, y: 218 };                   // a bench facing the water: its feet, seen from behind
function renderBench() {
  const p = pix(30, 16), K = '#14161f', WOOD = '#4a3426', WOODL = '#6a4a32';
  for (let x = 1; x < 29; x++) { p.set(x, 0, '#d8e0f0'); p.set(x, 1, WOODL); p.set(x, 2, WOOD); p.set(x, 4, WOODL); p.set(x, 5, WOOD); p.set(x, 7, '#c4cce0'); p.set(x, 8, WOOD); }
  for (const lx of [3, 26]) for (let y = 1; y < 16; y++) { p.set(lx, y, K); if (y > 9) p.set(lx + 1, y, '#2a2c3a'); }
  return p.done();
}
/* Evert Taube in bronze, playing his lute, on a granite plinth by the water: seen from behind, looking out over the water
   towards Stadshuset; snow on his head and shoulders */
const STATUE_ROWS = [
  '......hhh.....', '.....hhhhh....', '.....hhhhh....', '.....hhhhh....', '......hhh.....', '....ccccccc...', '...ccccccccc..', '..cccccccccc..',
  '..cccccccccLL.', '..ccccccccLLLL', '..ccccccccLLLL', '...cccccccLLLl', '...ccccccccLl.', '....ccccccccc.', '....ccccccccc.', '....ccccccccc.',
  '....ccccccccc.', '....cccccccc..', '....ccc..ccc..', '....ccc..ccc..', '....cc...cc...', '....cc...cc...', '....cc...cc...', '...ddd..ddd...'];
function renderStatue() {
  const p = pix(30, 40), B = ['#141210', '#221c16', '#33291e', '#473826', '#5e4a30', '#7a6040'], ox = 9;
  for (let y = 25; y < 40; y++) for (let x = 6; x < 25; x++) {                            // the plinth, snow on top, lit from the left lamp
    const col = y < 27 ? (y === 25 ? '#e0e6f2' : '#aab4cc') : y === 29 ? '#1e2030' : pick(['#262838', '#3a3c4e', '#585a6c', '#76727a'], 0.62 - (x - 6) * 0.022 - (y - 27) * 0.01, x, y);
    p.set(x, y, col);
  }
  STATUE_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    const ch = row[i]; if (ch === '.') continue;
    let v = ch === 'h' ? 0.35 : ch === 'L' ? 0.86 - (i - 10) * 0.06 + (j === 8 ? 0.1 : 0) : ch === 'l' ? 0.5 : ch === 'd' ? 0.1 : 0.3 - (i > 10 ? 0.08 : 0) + (i < 4 ? 0.12 : 0);
    if (ch === 'c' && row[i - 1] === '.') v += 0.28;                                      // the lamp's light down his left side
    p.set(ox + i - 1, 1 + j, pick(B, v, i, j, 0.35));
  } });
  for (let k = 0; k < 9; k++) p.set(ox + 1 - k, 8 - Math.round(k * 0.6), k < 7 ? B[2] : B[3]);   // the lute's neck, out past his left shoulder
  p.set(ox - 8, 2, B[3]); p.set(ox - 9, 3, B[2]); p.set(ox - 9, 2, B[4]);                 // its peg box, bent back
  for (const [x, y] of [[5, 0], [6, 0], [7, 0], [4, 1], [8, 1], [3, 5], [4, 5], [9, 5], [10, 5], [2, 6], [11, 6], [11, 8], [12, 8], [0, 7]])
    p.set(ox + x - 1, y, '#d8e0ee');                                                        // snow on his head, his shoulders, the lute
  return p.done();
}
/* a snowman the children built by the bench, a red tomte hat on him */
function renderSnowman() {
  const p = pix(17, 26);
  for (const [cx, cy, r] of [[8, 20, 5.3], [8, 12.5, 4.1], [8, 6.5, 3.1]]) for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
    const dx = x - cx, dy = y - cy, d = Math.hypot(dx, dy);
    if (d > r + 0.3) continue;
    const v = 0.62 + 0.22 * dx / r - 0.12 * dy / r - (d > r - 0.8 ? 0.12 : 0);
    p.set(x, y, dx > r * 0.35 && dy < r * 0.5 ? pick(WARMG, v + 0.05, x, y) : pick(SNOWG, v + 0.1, x, y));
  }
  for (const [x, y, c] of [[7, 6, '#141418'], [9, 6, '#141418'], [10, 7, '#e8782a'], [11, 7, '#c85a1a'], [7, 8, '#3a3a44'], [8, 9, '#3a3a44'], [9, 8, '#3a3a44'],
    [8, 12, '#141418'], [8, 14, '#141418'], [8, 19, '#141418']]) p.set(x, y, c);
  for (let x = 5; x <= 11; x++) p.set(x, 10, x % 2 ? '#c8323a' : '#2a6a40');                // a scarf
  p.set(10, 11, '#c8323a'); p.set(10, 12, '#2a6a40');
  p.line(4, 12, 0, 9, '#4a3020'); p.line(12, 12, 16, 9, '#4a3020'); p.set(0, 8, '#4a3020'); p.set(16, 8, '#4a3020');   // stick arms
  for (const [x0, x1, y] of [[8, 8, 0], [7, 8, 1], [6, 9, 2], [6, 10, 3]]) for (let x = x0; x <= x1; x++) p.set(x, y, x === x1 && y > 1 ? '#8a1a26' : '#c8283a');   // the tomte hat
  for (let x = 5; x <= 11; x++) p.set(x, 4, '#e8eef8');
  p.set(9, 0, '#f4f6fc');
  return p.done();
}
function renderLamp() {
  const p = pix(13, 98), K = '#12141c', KH = '#3a3e52', cx = 6;
  for (let y = 18; y < 94; y++) { p.set(cx, y, K); p.set(cx - 1, y, y < 60 ? KH : '#262838'); p.set(cx + 1, y, K); }
  for (const y of [30, 70]) for (let x = cx - 2; x <= cx + 2; x++) p.set(x, y, x === cx - 2 ? KH : K);
  for (let y = 86; y < 98; y++) { const hw = y < 90 ? 2 : 3; for (let x = cx - hw; x <= cx + hw; x++) p.set(x, y, x === cx - hw ? KH : K); }
  for (let x = cx - 3; x <= cx + 3; x++) { p.set(x, 86, '#c8d0e4'); }
  for (let y = 5; y <= 15; y++) {                                                         // the lantern, tapered, its glass lit
    const hw = y < 7 ? 3 : Math.round(3 - (y - 7) * 1.4 / 8);
    for (let x = cx - hw; x <= cx + hw; x++) p.set(x, y, x === cx - hw || x === cx + hw ? K : y < 9 ? '#fff6dc' : y < 12 ? '#ffe2a0' : '#ffc070');
    if (y >= 7) p.set(cx, y, '#c8904a');
  }
  for (let x = cx - 4; x <= cx + 4; x++) { p.set(x, 4, K); }
  for (let k = 0; k < 4; k++) for (let x = cx - 3 + k; x <= cx + 3 - k; x++) p.set(x, 3 - k, k === 0 ? K : '#d8e0f0');   // its cap, under snow
  p.set(cx, 0, K); p.set(cx, 16, K); p.set(cx, 17, K); p.set(cx - 1, 17, K); p.set(cx + 1, 17, K);
  return p.done();
}

/* ================= The rooftop neon sign on Södermalm (the city's one billboard): TOMTE GLÖGG, a steaming mug ================= */
const STEEL = { d: '#0e0f1a', m: '#1a1c2a', h: '#2e3248', l: '#5a6080' };
function renderBoard() {
  const p = pix(W, WL), L = BB.x - 2, R = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 4, BB.x + 20, BB.x + 36]) {                                  // legs down to the roof, braced
    for (let y = B; y <= BB_ROOF; y++) { p.set(lx, y, STEEL.m); p.set(lx + 1, y, STEEL.d); }
  }
  for (let k = 0; k < 2; k++) { p.line(BB.x + 5, B + 1, BB.x + 20, BB_ROOF - 1, STEEL.d); p.line(BB.x + 21, B + 1, BB.x + 36, BB_ROOF - 1, STEEL.d); }
  for (let x = L; x <= R; x++) p.set(x, B + 4, STEEL.h);                                 // a catwalk
  for (let y = T0; y <= B; y++) for (let x = L; x <= R; x++) {
    let col = STEEL.m;
    if (y === T0) col = '#c8d0e4'; else if (y === T0 + 1 || x === L) col = STEEL.h; else if (y === B || x === R) col = STEEL.d;
    p.set(x, y, col);
  }
  return trim(p);
}
const NEON_W = '#fff2e0', NEON_R = '#ff4a5c', NEON_G = '#ffd070';
const MUG = ['111111111..', '1.......1..', '1.......111', '1.......1.1', '1.......1.1', '1.......111', '1.......1..', '.1.....1...', '..11111....'];
const STEAM = [['.1', '1.', '.1', '1.', '.1'], ['1.', '.1', '1.', '.1', '1.'], ['.1', '1.', '.1', '1.', '.1']];
/** the ad: the dark panel, the tubes (lit or dim), their glow; lit = which parts are on */
function renderAd(lit) {
  const { w, h } = BB, p = pix(w, h), tubes = [];
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, (i === 0 || j === 0 || i === w - 1 || j === h - 1) ? '#1e2036' : '#0a0b1a');
  const tube = (x, y, col) => tubes.push([x, y, col]);
  MUG.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') tube(3 + i, 9 + j, NEON_R); });
  for (let i = 4; i < 11; i++) tube(i, 11, '#ff9a6a');                                    // the glögg in it
  if (lit.steam !== undefined) STEAM.forEach((S, k) => { if (k === lit.steam) S.forEach((row, j) => { for (let i = 0; i < 2; i++) if (row[i] === '1') tube(4 + k * 2 + i, 3 + j, NEON_W); }); });
  const cells = [];                                                                       // the letters' boxes: their gaps stay dark
  const letters = (s, x, y, col) => {
    let cx = x;
    for (const ch of s) {
      const rows = GLYPHS[ch], gw = rows[0].length;
      rows.forEach((row, j) => { for (let i = 0; i < gw; i++) if (row[i] === '1') tube(cx + i, y + j, col); });
      cells.push([cx, y, cx + gw - 1, y + 4]);
      cx += gw + 1;
    }
  };
  letters('TOMTE', 19, 3, lit.white ? NEON_W : '#5a5450');
  letters('GLÖGG', 20, 11, lit.red ? NEON_R : '#5a2a30');
  if (lit.star) for (const [dx, dy] of [[1, 0], [0, 1], [1, 1], [2, 1], [1, 2]]) tube(41 + dx, 2 + dy, NEON_G);
  const on = new Set(tubes.map(([x, y]) => x + ',' + y));
  for (const [x, y, col] of tubes) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    if ((!dx && !dy) || on.has((x + dx) + ',' + (y + dy))) continue;
    const X = x + dx, Y = y + dy;
    if (cells.some(([a, b, c, d]) => X >= a && X <= c && Y >= b && Y <= d)) continue;
    if (X > 0 && Y > 0 && X < w - 1 && Y < h - 1) p.set(X, Y, mixHex('#0a0b1a', col, col === '#5a5450' || col === '#5a2a30' ? 0.08 : 0.28));
  }
  for (const [x, y, col] of tubes) p.set(x, y, col);
  return p.done();
}
const GLYPHS = {
  T: ['111', '010', '010', '010', '010'], O: ['010', '101', '101', '101', '010'], M: ['10001', '11011', '10101', '10001', '10001'],
  'Ö': ['101', '000', '111', '101', '111'],                                               // the dots, a clear row, a short O
  E: ['111', '100', '110', '100', '111'], G: ['011', '100', '101', '101', '011'], L: ['100', '100', '100', '100', '111'],
};

/* ================= People ================= */
// One function draws everyone (after rome.js): a look gives the proportions for a height and the colours dimmed for the
// night; person() puts it on its feet. Winter clothes: knitted hats with a bobble, scarves, long coats. The edge of the
// shoulders and the hair catch the warm light of the hall across the water; snow settles on hats and shoulders.
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#0c1026', GLOWC = '#ffd8a8', FLAKE = '#dfe6f4';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.28, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0,
    hat: o.hat ? m(o.hat) : null, pom: o.pom ? m(o.pom) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind (looking at the view), 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (camera, cup), 3 out, 4 up and out (pointing high), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated; o.bend: bent forward (profile); o.snow: flakes on him
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1;
    ctx.fillStyle = 'rgba(16,20,48,0.32)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th + P.coat, P.top);                                                    // the coat, longer for some
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                            // the hall's light on the shoulders
  if (P.scarf) R(tx + 1, ty, bw - 2, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else { R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 1 - k + 1, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) {                                                                         // a knitted hat, a bobble on top
    R(hx, hY - 1, hw, 2 + (view === 0 ? 1 : 0), P.hat); if (!side) R(hx - (view === 1 ? 0 : 0), hY + 1, hw, 1, mixHex(P.hat, NIGHT, 0.3));
    if (P.pom) R(hx + (hw >> 1) - (side ? dir > 0 ? 1 : 0 : 0), hY - 2, 2, 1, P.pom);
  }
  if (o.snow) { ctx.fillStyle = FLAKE; ctx.fillRect(hx + 1, hY - (P.hat ? 1 : 0), 1, 1); ctx.fillRect(tx + 1, ty, 1, 1); ctx.fillRect(tx + bw - 2, ty, 1, 1); }
  return hY;
}
const L_HE = look(24, 1, 0, '#34466e', '#1e2030', { scarf: '#c8323a', coat: 2 });
const L_SHE = look(22, 0, 3, '#c8b496', '#2a2a3a', { hat: '#ece6dc', pom: '#ece6dc', long: 3, coat: 3 });
const L_KID = look(14, 0, 2, '#e04a3a', '#d8423a', { hat: '#2a6ab8', pom: '#f0e040', dk: 0.2 });
const L_PARENT = look(23, 2, 1, '#566a44', '#262838', { hat: '#8a2a3a', coat: 2 });
const L_PHOTO = look(24, 1, 1, '#2c2c34', '#1e2030', { hat: '#3a3a46', scarf: '#d8a030', coat: 2 });
const L_BENCH = look(23, 0, 5, '#7a4a3a', '#262838', { hat: '#3a3030' });
const L_DOGW = look(27, 3, 0, '#9a3a3a', '#2a2a38', { hat: '#e8e0d0', pom: '#c8323a', dk: 0.32, coat: 2 });
const L_JOG = look(29, 0, 4, '#2f8a6a', '#1a1c28', { hat: '#f0c030', pom: '#f0c030', dk: 0.32 });
const L_PULL = look(27, 2, 2, '#3a5a8a', '#1e2030', { hat: '#d8d0c0', pom: '#3a5a8a', dk: 0.32, coat: 2 });
const L_RIDER = look(12, 0, 3, '#f0b030', '#e8a020', { hat: '#c8323a', pom: '#f4f0e8', dk: 0.26 });
const SNOWMAN = { x: 418, fy: 255 };
const COUPLE = { x: 196, gap: 8 }, KID = { x: 148 }, PARENT = { x: 134 }, PHOTO = { x: 368, tri: 356 };
const DOG_ROWS = [
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '...dd..dd...', '..d..d.d..d.'],
  ['............', '............', 't...........', '.tlllllllll.', '..ddddddddee', '..d.d..d.ddn', '..d.d..d.d..'],   // nose down in the snow
];
const DOG_KEY = { d: '#9a7040', l: '#c09058', t: '#9a7040', e: '#6a4a2a', n: '#1a1210' };

/* ================= Snow, the city's own: three depths, lit where it passes a lamp or the floodlights ================= */
const SNOW_L = [
  { count: 210, nMin: 18, nMax: 26, size: 1, col: 'rgba(168,184,222,0.55)', amp: 2.5, drift: -8, y1: [96, WL], lights: false },
  { count: 140, nMin: 28, nMax: 36, size: 1, col: 'rgba(220,230,250,0.85)', amp: 4.5, drift: -16, y1: [WL + 2, QY], lights: true },
  { count: 56, nMin: 40, nMax: 50, size: 2, col: 'rgba(240,244,255,0.92)', amp: 8, drift: -28, y1: [H + 4, H + 4], lights: true },
];
SNOW_L.forEach((L, li) => {
  const r = rng(300 + li);
  L.flakes = [];
  for (let i = 0; i < L.count; i++) L.flakes.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r() });
});
// warm light volumes that tint the flakes passing through them: [cx, cy, rx, ry, colour]
const SNOW_LIGHTS = [
  ...LAMPS.map(l => [l.x, l.y + 30, 40, 56, 'rgba(255,214,160,0.98)']), [T.cx, 140, 30, 40, 'rgba(255,190,140,0.95)'],
  [440, 150, 26, 14, 'rgba(255,220,170,0.95)'],
];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, [BB.x + (BB.w >> 1), BB.y + 10, 34, 20, 'rgba(255,170,180,0.95)']];   // + the neon sign
// a picked weather (site/js/engine/weather.js): Stockholm draws its own snow, and the snow lying on the roofs and the
// terrace stays in every pick. Far drops end on the far shores, mid ones on the water, near ones on the terrace
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[100, WL], [WL + 2, QY - 1], [QY + 6, H - 2]];
const wxHex = c => '#' + c.match(/\d+/g).slice(0, 3).map(v => (+v).toString(16).padStart(2, '0')).join('');
const wxLights = list => list.map(([x, y, rx, ry, c]) => ({ x, y, rx, ry, col: wxHex(c) }));
const WX = { land: WX_LAND, lights: wxLights(SNOW_LIGHTS), leaves: { wind: [0, 30, W, 196] } };
const WX_BB = { ...WX, lights: wxLights(SNOW_LIGHTS_BB) };

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, SOD_C, HALL_C, RIGHT_C, STEAM_C, STEAM_L, FERRY_C, FERRY_BARE, WATER_C, MIRROR_C, TERR_C,
  BENCH_C, STATUE_C, LAMP_C, SNOWMAN_C, BOARD_C, ADS, PITCH, SWAN_SPR, DUCK_SPR, GULL_SPR, GULL_WARM, GULL_SIT_C, DOG_SPR, PUFFS, VIG_C;
const SPLIT = {};                                   // where each layer's windows start and end in WINS
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 8, 20], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function makePuff(r, col) {
  const s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - r, y - r) / r; if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) p.set(i, y, y < r * 0.7 ? col : mixHex(col, '#3a3c52', 0.35)); }
  return p.done();
}
function prerender() {
  for (const L of [WINS, VB_LAMPS, SOD_LAMPS, HALL_LIGHTS, STEAM_BULBS]) L.length = 0;
  HL = {
    moon: makeHalo(40, '#a8a4c8', 0.16, 1.8), moonCore: makeHalo(12, '#dcd8ec', 0.26, 1.5),
    tower: makeHalo(64, '#ff9a50', 0.12, 1.6), towerBase: makeHalo(34, '#ffb070', 0.16, 1.5), hall: makeHalo(44, '#ff9a58', 0.1, 1.6),
    lantern: makeHalo(20, '#ffd080', 0.32, 1.5), crowns: makeHalo(9, '#ffe090', 0.42, 1.3), dome: makeHalo(7, '#ffe090', 0.36, 1.3),
    bell: makeHalo(42, '#ffd890', 0.2, 1.5), pool: makeHalo(14, '#ffa860', 0.14, 1.5), win: makeHalo(5, '#ffb860', 0.12, 1.4),
    lamp: makeHalo(60, '#ffc070', 0.24, 1.7), lampCore: makeHalo(14, '#fff0c8', 0.5, 1.3), qlamp: makeHalo(6, '#ffd890', 0.4, 1.2),
    tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), red: makeHalo(3, '#ff4040', 0.5, 1), neon: makeHalo(34, '#ff5a6a', 0.13, 1.6),
    neonW: makeHalo(18, '#ffe8d8', 0.1, 1.5), steam: makeHalo(24, '#ffc878', 0.15, 1.5), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2),
    statue: makeHalo(22, '#ffcf98', 0.1, 1.5), deck: makeHalo(2, '#ffd890', 0.45, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  const r = rng(1923);
  SPLIT.far = WINS.length; { const p = pix(W, WL); paintFarWest(p, r); FAR_C = trim(p); }
  SPLIT.sod = WINS.length; { const p = pix(W, WL); paintSodermalm(p, r); SOD_C = trim(p); }
  SPLIT.hall = WINS.length; { const p = pix(W, WL); paintInner(p); paintWing(p, r, 'S'); paintWing(p, r, 'E'); paintSmallTower(p); paintTower(p, r); HALL_C = trim(p); }
  SPLIT.right = WINS.length; { const p = pix(W, WL); paintRight(p, r); RIGHT_C = trim(p); }
  SPLIT.end = WINS.length;
  STEAM_C = renderSteamer(); STEAM_L = renderSteamer(true);
  FERRY_C = [renderFerry(true), null]; FERRY_C[1] = flipC(FERRY_C[0]); FERRY_BARE = [renderFerry(false), null]; FERRY_BARE[1] = flipC(FERRY_BARE[0]);
  BOARD_C = renderBoard();
  ADS = [0, 1, 2].map(steam => [0, 1].map(red => [0, 1].map(star => renderAd({ steam, white: true, red: !!red, star: !!star }))));
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  WATER_C = renderWater();
  {                                                                                      // the mirror's source: the still far side
    const [c, x] = makeCanvas(W, WL);
    x.drawImage(SKY_C, 0, 0);
    for (const L of [FAR_C, SOD_C, HALL_C, RIGHT_C]) x.drawImage(L.c, L.x, L.y);
    for (const w of WINS) if (w.mode === 'on') { x.fillStyle = w.c1; x.fillRect(w.x, w.y, w.w, w.h); }
    x.drawImage(STEAM_C, MOOR.x, WL + 1 - STM.h);
    MIRROR_C = renderMirror(c);
  }
  TERR_C = renderTerrace(); BENCH_C = renderBench(); STATUE_C = renderStatue(); LAMP_C = renderLamp(); SNOWMAN_C = renderSnowman();
  SWAN_SPR = [sprite(SWAN, SWAN_KEY), sprite(SWAN, SWAN_KEY, true)]; DUCK_SPR = [sprite(DUCK, DUCK_KEY), sprite(DUCK, DUCK_KEY, true)];
  GULL_SPR = GULL_F.map(rows => [sprite(rows, GULL_KEY), sprite(rows, GULL_KEY, true)]);
  const warm = { w: '#ffe6cc', g: '#d8a888', k: '#4a2a28', y: '#f0b040' };
  GULL_WARM = GULL_F.map(rows => [sprite(rows, warm), sprite(rows, warm, true)]);
  const sitKey = { w: '#e8ecf4', g: '#9aa4c0', y: '#f0b040', k: '#e8a040' };
  GULL_SIT_C = [sprite(GULL_SIT_ROWS, sitKey), sprite(GULL_PECK_ROWS, sitKey)];
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  PUFFS = [1, 2, 3, 4, 5].map(rr => makePuff(rr, '#9a9cb4'));
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8ecff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 3));
  ctx.globalAlpha = 0.8; ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6); ctx.globalAlpha = 1;
  halo(HL.moonCore, MOON.x, MOON.y, 0.8);
  for (const L of CLOUDS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y); }
}
const winLit = (w, t) => w.mode === 'on' || (w.mode === 'tog' && frac(w.k * t / LOOP + w.ph) < w.duty);
function drawWins(a, b, t, halos) {
  for (let i = a; i < b; i++) {
    const w = WINS[i];
    if (winLit(w, t)) {
      if (w.h > 1) { const half = w.h >> 1; R(w.x, w.y, w.w, half, w.c1); R(w.x, w.y + half, w.w, w.h - half, w.c2); }
      else R(w.x, w.y, w.w, 1, w.c1);
      if (halos && i % 4 === 0) halo(HL.win, w.x + (w.w >> 1), w.y + (w.h >> 1), 0.8);
    }
    if (w.deco) drawDeco(w);
  }
}

/* ================= Per-frame: the far west, Södermalm, the neon sign ================= */
const VB_CARS = [[1, 7, 0.1], [-1, 6, 0.4], [1, 9, 0.62], [-1, 8, 0.85], [1, 5, 0.33], [-1, 11, 0.2]];   // dir, crossings a loop, phase
const QUAY_CARS = [[1, 6, 0.15, 4, 116, WL - 5], [-1, 5, 0.6, 4, 116, WL - 5], [1, 7, 0.4, 188, 238, WL - 4], [-1, 4, 0.8, 188, 238, WL - 4],
  [1, 6, 0.7, 404, 480, WL - 6], [-1, 8, 0.05, 404, 480, WL - 6]];                          // dir, k, phase, x0, x1, row
const TINY = [];                                    // little walkers on the far quays: { x0, x1, y, k, ph, dir, rank, coat }
{
  const r = rng(515);
  for (const [x0, x1, y, n] of [[2, 112, WL - 4, 5], [188, 238, WL - 3, 3], [404, 480, WL - 5, 4]]) for (let i = 0; i < n; i++)
    TINY.push({ x0, x1, y, k: 2 + Math.floor(r() * 4), ph: r(), dir: r() < 0.5 ? 1 : -1, rank: frac((TINY.length + 0.5) * 0.618), coat: ['#c8423a', '#2a3a5a', '#e8e0d0', '#3a6a4a', '#8a8e9e'][Math.floor(r() * 5)] });
}
function drawFar(t) {
  blit(FAR_C);
  drawWins(SPLIT.far, SPLIT.sod, t, false);
  ctx.fillStyle = '#ffe0a0';
  for (const x of VB_LAMPS) ctx.fillRect(x, VB.deck - 2, 1, 1);
  for (const [d, k, ph] of VB_CARS) {
    const u = frac(k * t / LOOP + ph), x = Math.round(d > 0 ? lerp(VB.x0, VB.x1, u) : lerp(VB.x1, VB.x0, u));
    if (x >= NM.x0) continue;
    dot(x, VB.deck - 1, d > 0 ? '#fff6e0' : '#ff5040');
  }
  for (const [d, k, ph, x0, x1, y] of QUAY_CARS.slice(2, 4)) { const x = Math.round(lerp(d > 0 ? x0 : x1, d > 0 ? x1 : x0, frac(k * t / LOOP + ph))); dot(x, y, '#fff2d8'); dot(x - d, y, '#ff5040'); }
}
function drawTiny(t, crowd, x0) {
  if (nobody(crowd)) return;
  for (const p of TINY) {
    if (p.x0 !== x0 || p.rank >= crowd) continue;
    const u = frac(p.k * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? lerp(p.x0, p.x1, u) : lerp(p.x1, p.x0, u)), f = step(t, 960) + (x & 1);
    R(x, p.y - 4, 1, 1, '#d8b090'); R(x, p.y - 3, 1, 2, p.coat); R(x + ((f & 1) ? 1 : -1) * 0, p.y - 1, 1, 1, '#14141c'); if (f & 1) R(x + 1, p.y - 1, 1, 1, '#14141c');
  }
}
function drawSod(t, crowd) {
  blit(SOD_C);
  drawWins(SPLIT.sod, SPLIT.hall, t, true);
  for (const l of SOD_LAMPS) { dot(l.x, l.y, '#fff0c8'); halo(HL.qlamp, l.x, l.y, 0.8); }
  for (let x = 6; x < SOD_X1 - 4; x += 16) { dot(x, WL - 8, '#ffe8b0'); halo(HL.qlamp, x, WL - 8, 0.7); }   // Söder Mälarstrand's lamps
  for (const [d, k, ph, x0, x1, y] of QUAY_CARS.slice(0, 2)) { const x = Math.round(lerp(d > 0 ? x0 : x1, d > 0 ? x1 : x0, frac(k * t / LOOP + ph))); dot(x, y, '#fff2d8'); dot(x - d, y, '#ff5040'); halo(HL.tiny, x, y, 0.6); }
  drawTiny(t, crowd, 2);
}
const BB_FLICK = [[52.2, 53.6], [171.4, 172.2]];    // the red tubes stutter now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.neon, BB.x + 30, BB.y + 13, fl ? 0.6 : 0.95 + 0.05 * wave(t, 8));
  halo(HL.neonW, BB.x + 28, BB.y + 5, 0.9);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(ADS[step(t, 480) % 3][fl ? 0 : 1][step(t, 160) % 3 ? 1 : 0], BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
}

/* ================= Per-frame: Stadshuset ================= */
const bellLevel = t => { let b = 0; for (const tb of BELLS) { const d = t - tb; if (d >= 0 && d < 3) b = Math.max(b, Math.exp(-d * 1.2) * (1 - d / 3)); } return b; };
function drawHall(t) {
  halo(HL.tower, T.cx, 112, 0.95);
  halo(HL.hall, 268, 148, 0.8); halo(HL.hall, 372, 146, 0.8);
  blit(HALL_C);
  drawWins(SPLIT.hall, SPLIT.right, t, true);
  for (const q of POOLS) halo(HL.pool, q, WL - 4, 0.9);
  halo(HL.towerBase, T.se, WL - 8, 0.95);
  HALL_LIGHTS.forEach((l, i) => { dot(l.x, l.y, '#ffd890'); if (i % 2) halo(HL.tiny, l.x, l.y, 0.7 + 0.2 * wave(t, 300 + i * 17)); });
  const b = bellLevel(t);
  halo(HL.lantern, T.cx, 53, 0.8 + 0.1 * wave(t, 6) + b * 0.6);
  halo(HL.crowns, T.cx, 25, 0.75 + 0.15 * wave(t, 10, 0.3) + b * 0.4);
  halo(HL.dome, SMALL.cx, 97, 0.7 + 0.1 * wave(t, 8, 0.6));
  if (b > 0.02) halo(HL.bell, T.cx, 70, b);
  for (const tb of BELLS) {                                                              // the strokes, widening from the belfry
    const d = t - tb;
    if (d < 0 || d > 2.4) continue;
    const rr = 6 + d * 30, n = Math.floor(rr * 2.2);
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890'; ctx.globalAlpha = (1 - d / 2.4) * 0.45;
    for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(T.cx + Math.cos(a) * rr), Math.round(80 + Math.sin(a) * rr * 0.8), 1, 1); }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
function drawRight(t, crowd) {
  blit(RIGHT_C);
  drawWins(SPLIT.right, SPLIT.end, t, false);
  for (const lx of QLAMPS) { dot(lx, WL - 14, '#fff0c8'); halo(HL.qlamp, lx, WL - 14, 0.85); }
  for (const [d, k, ph, x0, x1, y] of QUAY_CARS.slice(4)) { const x = Math.round(lerp(d > 0 ? x0 : x1, d > 0 ? x1 : x0, frac(k * t / LOOP + ph))); dot(x, y, '#fff2d8'); dot(x - d, y, '#ff5040'); }
  drawTiny(t, crowd, 404);
  drawTiny(t, crowd, 188);
}

/* ================= Per-frame: the water, and what floats on it ================= */
const MOOR = { x: 412 };                            // the steamboat moored at Stadshusbron
function drawWater(t, billboard) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = QY - WL;
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.06) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.6);
    ctx.globalAlpha = 0.92 * (1 - k / (rows + 16)) * (0.84 + 0.16 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(MIRROR_C, 0, k, W, 1, off, WL + k, W, 1);
  }
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    ctx.globalAlpha = 0.5 * Math.min(1, v + 0.1);
    ctx.fillStyle = g.c; ctx.fillRect(Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, QY);
  if (billboard) streak(ctx, t, BB.x + 26, 6, '#ff6a7a', 0.14, 21, WL + 1, QY);
}
/** a sprite on the water at row wy, its broken mirror image under it */
function drawFloat(spr, x, wy, t, a = 0.3) {
  const w = spr.width, h = spr.height, n = Math.min(h, 14);
  for (let j = 0; j < n; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3 + wy) * (0.5 + j * 0.1));
    ctx.globalAlpha = a * (1 - j / n);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wy + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, wy - h + 1);
}
function drawWake(x, wy, dir, n, t) {
  const st = step(t, 1440);
  for (let k = 0; k < n; k++) {
    if (hash(k, st, 17) > 0.84 - k * 0.012) continue;
    ctx.globalAlpha = 0.55 * (1 - k / n);
    dot(x - dir * k * 3, wy + 1 + k * 0.25, k < 5 ? '#dfe6f4' : '#8a98c0');
  }
  ctx.globalAlpha = 1;
}
function drawSmoke(fx, fy, t, n, drift, burst) {
  for (let k = 0; k < 6; k++) {
    const { u } = cycle(t, n, k / 6), rr = Math.min(5, 1 + Math.floor(u * (burst ? 6 : 4)));
    ctx.globalAlpha = (burst ? 0.55 : 0.3) * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(PUFFS[rr - 1], Math.round(fx + u * drift + wave(t, 31, k * 0.2) * 1.5 * u) - rr, Math.round(fy - u * 18) - rr);
  }
  ctx.globalAlpha = 1;
}
const BULB_COLS = ['#ffe6a0', '#fff4d8', '#ff8a6a', '#ffe6a0', '#9ad0ff'];
function drawBulbs(x, y, t, seed) {
  const s = step(t, 160);
  STEAM_BULBS.forEach(([bx, by], i) => { if (hash(i, s + seed, 5) > 0.12) dot(x + bx, y + by, BULB_COLS[(i + seed) % 5]); });
}
function drawBoats(t, crowd) {
  const top = WL + 1 - STM.h + 1;
  halo(HL.steam, MOOR.x + 30, WL - 8, 0.8);
  drawFloat(STEAM_C, MOOR.x, WL + 1, t, 0.28);
  drawBulbs(MOOR.x, top, t, 0);
  drawSmoke(MOOR.x + 31, top - 1, t, 30, -10, false);
  if (t >= STEAMER.t0 && t < STEAMER.t1) {                                               // a steamboat heads west on a Christmas cruise
    const u = (t - STEAMER.t0) / (STEAMER.t1 - STEAMER.t0), x = Math.round(lerp(W + 4, -STM.w - 10, u)), wy = 171, y0 = wy - STM.h + 1;
    halo(HL.steam, x + 30, wy - 8, 0.9);
    drawWake(x + STM.w, wy, -1, 26, t);
    drawFloat(STEAM_L, x, wy, t, 0.3);
    drawBulbs(x, y0, t, 3);
    dot(x - 1, wy - 1, '#eef2fc'); dot(x - 2, wy, '#c8d6f0');                            // the bow wave
    const horn = t >= STEAMER.horn && t < STEAMER.horn + 2.5;
    drawSmoke(x + 31, y0 - 1, t, 26, 22, horn);
    if (horn) { ctx.globalAlpha = 0.6 * (1 - (t - STEAMER.horn) / 2.5); for (let k = 0; k < 4; k++) dot(x + 29 - k, y0 - 2 - k, '#f4f6ff'); ctx.globalAlpha = 1; }
  }
  for (const F of FERRY) {                                                               // the commuter boat across Riddarfjärden
    if (t < F.t0 || t >= F.t1) continue;
    const u = (t - F.t0) / (F.t1 - F.t0), wy = 184, x = Math.round(F.dir > 0 ? lerp(-FRY.w - 4, W + 4, u) : lerp(W + 4, -FRY.w - 4, u));
    const spr = (nobody(crowd) ? FERRY_BARE : FERRY_C)[F.dir > 0 ? 0 : 1];
    drawWake(F.dir > 0 ? x : x + FRY.w, wy, F.dir, 22, t);
    drawFloat(spr, x, wy, t, 0.3);
    const mx = F.dir > 0 ? x + 29 : x + FRY.w - 30;
    halo(HL.tiny, mx, wy - FRY.h + 1, 0.9); halo(HL.steam, x + (FRY.w >> 1), wy - 7, 0.5);
    dot(F.dir > 0 ? x : x + FRY.w - 1, wy - 5, '#ff3a30');
  }
  for (const lag of [0, 0.03]) {                                    // two swans on their rounds by the edge
    const a = TAU * (2 * t / LOOP - lag), sx = 288 + 52 * Math.sin(a) + 8 * Math.sin(a * 3), sy = 191 + 2 * Math.sin(TAU * 3 * t / LOOP + lag * 9);
    const d = Math.cos(a) + 0.43 * Math.cos(a * 3) >= 0 ? 0 : 1, X = Math.round(sx), Y = Math.round(sy);
    ctx.globalAlpha = 0.25; box(X - 4, Y + 1, 9, 1, '#dfe8f8'); ctx.globalAlpha = 1;
    ctx.drawImage(SWAN_SPR[d], X - 4, Y - 7);
  }
  for (let i = 0; i < 3; i++) {                                                          // three ducks paddling in a line
    const a = TAU * (t / LOOP - i * 0.012), X = Math.round(118 + 64 * Math.sin(a)), Y = 195 + (i & 1), d = Math.cos(a) >= 0 ? 0 : 1;
    ctx.globalAlpha = 0.3; box(X - 1, Y + 1, 5, 1, '#8a98c0'); ctx.globalAlpha = 1;
    ctx.drawImage(DUCK_SPR[d], X - 2, Y - 3);
  }
}

/* ================= Per-frame: gulls ================= */
const SKY_GULLS = [{ cx: 170, cy: 42, ax: 150, ay: 12, kx: 3, ky: 7, px: 0.1, py: 0.3 }, { cx: 390, cy: 76, ax: 80, ay: 14, kx: 4, ky: 9, px: 0.6, py: 0.1 }];
const TOWER_GULLS = [{ rx: 30, ry: 10, cy: 76, k: 7, ph: 0 }, { rx: 24, ry: 8, cy: 104, k: -8, ph: 0.4 }, { rx: 38, ry: 12, cy: 60, k: 6, ph: 0.75 }];
function drawSkyGulls(t) {
  SKY_GULLS.forEach((g, i) => {
    const a = TAU * (g.kx * t / LOOP + g.px), x = g.cx + g.ax * Math.sin(a), y = g.cy + g.ay * Math.sin(TAU * (g.ky * t / LOOP + g.py)), vx = Math.cos(a);
    ctx.drawImage(GULL_SPR[Math.abs(vx) < 0.25 ? 1 : [0, 1, 2, 1][(step(t, 720) + i) & 3]][vx >= 0 ? 0 : 1], Math.round(x) - 4, Math.round(y) - 2);
  });
}
function drawTowerGulls(t, front) {
  TOWER_GULLS.forEach((g, i) => {
    const a = TAU * (g.k * t / LOOP + g.ph), z = Math.cos(a);
    if ((z > 0) !== front) return;
    const x = T.cx + g.rx * Math.sin(a), y = g.cy + g.ry * z * 0.5 + 3 * Math.sin(a * 2), right = g.k * z > 0;
    ctx.drawImage((front ? GULL_WARM : GULL_SPR)[[0, 1, 2, 1][(step(t, 720) + i * 2) & 3]][right ? 0 : 1], Math.round(x) - 4, Math.round(y) - 2);
  });
}
function drawStatueGull(t) {
  const gx = STATUE.x - 3, gy = STATUE.fy - 30;
  if (t < GULL_SIT.land - 3 || t >= GULL_SIT.off + 4) return;
  if (t < GULL_SIT.land) { const u = (t - GULL_SIT.land + 3) / 3; ctx.drawImage(GULL_SPR[step(t, 960) % 3][1], Math.round(lerp(gx + 80, gx - 2, u)), Math.round(lerp(120, gy - 2, u * u))); }
  else if (t < GULL_SIT.off) ctx.drawImage(GULL_SIT_C[hash(step(t, 240), 17) < 0.3 ? 1 : 0], gx, gy);
  else { const u = (t - GULL_SIT.off) / 4; ctx.drawImage(GULL_SPR[step(t, 1440) % 3][1], Math.round(lerp(gx - 2, gx - 110, u)), Math.round(lerp(gy - 2, 110, Math.sqrt(u)))); }
}

/* ================= Per-frame: the terrace and the people on it ================= */
const SPARKS = [];                                  // the snow glitters under the lamps
{
  const r = rng(616);
  while (SPARKS.length < 260) {
    const x = Math.floor(r() * W), y = QY + 6 + Math.floor(r() * (H - QY - 8)), g = groundLight(x, y);
    if (r() < 0.35 + g * 0.65) SPARKS.push({ x, y, k: 3 + Math.floor(r() * 6), ph: r(), c: g > 0.35 ? '#fff4dc' : '#eef4ff' });
  }
}
function drawSnowSparks(t) {
  for (const s of SPARKS) if (frac(s.k * t / LOOP + s.ph) < 0.07) { ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
function drawCouple(t, snow) {
  const kiss = t >= KISS[0] && t < KISS[1], pt = t >= POINT[0] && t < POINT[1] && frac(t / 4) < 0.75;
  const lean = !kiss && frac(t / 40 + 0.2) < 0.16, he = COUPLE.x, she = COUPLE.x + COUPLE.gap;
  if (kiss) {
    person(she, EDGE, L_SHE, { view: 2, dir: -1, lean: -2, snow });
    person(he, EDGE, L_HE, { view: 2, dir: 1, aR: 3, lean: 2, snow });
  } else {
    person(she, EDGE, L_SHE, { view: 0, aR: pt ? 4 : 0, lean: lean ? -1 : 0, snow });
    person(he, EDGE, L_HE, { view: 0, aR: 5, lean: lean ? 1 : 0, snow });
  }
}
function drawKid(t, snow) {
  const catching = CATCH.some(([a, b]) => t >= a && t < b), s = step(t, 480);
  person(PARENT.x, EDGE + 1, L_PARENT, { view: 2, dir: 1, aR: frac(t / 30) < 0.14 ? 2 : catching && s % 8 < 3 ? 3 : 0, snow });
  if (frac(t / 30) < 0.14 && frac(t / 30) > 0.02) {                                      // steam off the parent's cup of coffee
    const { u } = cycle(t, 480, 0), ty = tyOf(L_PARENT, EDGE + 1);
    ctx.globalAlpha = 0.4 * (1 - u); dot(PARENT.x + 4, ty - 2 - u * 4, '#d8deec'); ctx.globalAlpha = 1;
  }
  if (catching) {
    const hop = s % 6 === 0 ? 2 : s % 6 === 1 ? 1 : 0, up = s % 4 < 2;
    person(KID.x, EDGE + 1 - hop, L_KID, { view: 1, aL: 1, aR: up ? 1 : 3, snow });
  } else {
    const h = hash(step(t, 60), 7);
    person(KID.x, EDGE + 1, L_KID, { view: h < 0.25 ? 1 : 0, aR: h > 0.78 ? 3 : 0, snow });   // looks at the water, points at the swans
  }
}
function drawPhoto(t, snow) {
  const u = frac(t / 24 + 0.3), x = PHOTO.x, tx = PHOTO.tri, fy = EDGE + 1;
  ctx.fillStyle = '#2a2c3a';
  for (const lx of [tx - 4, tx, tx + 3]) { const n = fy - 189; for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(tx, lx, k / n)), 189 + k, 1, 1); }
  R(tx - 1, 186, 4, 3, '#16161e'); R(tx - 2, 187, 1, 1, '#3a3c4a'); R(tx, 186, 2, 1, '#3a3c4a');
  if (u < 0.58) {
    if (frac(t) < 0.5) { dot(tx + 2, 188, '#ff3030'); halo(HL.red, tx + 2, 188, 0.6); }
    person(x, fy, L_PHOTO, { view: 2, dir: -1, bend: true, aR: 2, snow });
  } else if (u < 0.82) {
    person(x, fy, L_PHOTO, { view: 2, dir: -1, aR: 3, snow });
    const ty = tyOf(L_PHOTO, fy);
    R(x - 5, ty + 1, 2, 2, '#9ad0ff'); halo(HL.screen, x - 4, ty + 2, 0.9);
  } else person(x, fy, L_PHOTO, { view: 0, snow });
}
function drawSitter(t, snow) {
  const sip = frac(t / 20 + 0.6) < 0.16, x = BENCH.x + 13, fy = BENCH.y - 11;
  person(x, fy, L_BENCH, { view: 0, sit: true, aR: sip ? 2 : 0, snow });
  const ty = tyOf({ ll: 0, th: L_BENCH.th }, fy);
  R(x + 5, ty + (sip ? -2 : 4), 1, 2, '#c8ccd8');                                         // the thermos cup
  for (let k = 0; k < 3; k++) {
    const { u } = cycle(t, 300, k / 3);
    ctx.globalAlpha = 0.35 * (1 - u); dot(x + 5 + wave(t, 40, k * 0.3), ty + (sip ? -3 : 3) - u * 6, '#dfe4f0'); ctx.globalAlpha = 1;
  }
}
function drawDogWalker(t, snow) {
  for (const D of DOGW) {
    if (t < D.t0 || t >= D.t1) continue;
    const d = D.dir, mid = d > 0 ? 226 : 262;
    const s = along([[D.t0, d > 0 ? -24 : W + 24], [D.t0 + 15, mid], [D.t0 + 21, mid], [D.t1, d > 0 ? W + 40 : -40]], t);
    const x = Math.round(s.x), dx = x + d * 15, df = s.moving ? (Math.floor(x / 3) & 1) : (hash(step(t, 240), 3) < 0.7 ? 2 : 0);
    ctx.drawImage(DOG_SPR[df][d > 0 ? 0 : 1], dx - 6, LANE_DOG - 6);
    ctx.fillStyle = 'rgba(16,20,48,0.3)'; ctx.fillRect(dx - 4, LANE_DOG + 1, 9, 1);
    person(x, LANE_DOG, L_DOGW, { view: 2, dir: d, step: s.moving && (Math.floor(x / 4) & 1), aR: 3, snow });
    const hx = x + d * 4, hy = tyOf(L_DOGW, LANE_DOG) + 2, cx = dx + d * 2, cy = LANE_DOG - 3, n = Math.abs(cx - hx);
    ctx.fillStyle = '#c8323a';
    for (let k = 1; k < n; k++) ctx.fillRect(hx + Math.sign(cx - hx) * k, Math.round(lerp(hy, cy, k / n) + Math.sin(Math.PI * k / n) * 2), 1, 1);
  }
}
function drawJogger(t, snow) {
  for (const J of JOG) {
    if (t < J.t0 || t >= J.t1) continue;
    const u = (t - J.t0) / (J.t1 - J.t0), d = J.dir, x = Math.round(d > 0 ? lerp(-20, W + 20, u) : lerp(W + 20, -20, u)), f = Math.floor(x / 5) & 1;
    const hy = person(x, LANE_RUN - f, L_JOG, { view: 2, dir: d, step: f, aR: 4, snow });
    for (let k = 0; k < 2; k++) {                                                        // breath in the cold air
      const { u: v } = cycle(t, 300, k / 2);
      ctx.globalAlpha = 0.45 * (1 - v); dot(x + d * (4 + v * 6), hy + 3 - v * 2, '#e4eaf6'); ctx.globalAlpha = 1;
    }
  }
}
function drawPulka(t, snow) {
  for (const P of PULKA) {
    if (t < P.t0 || t >= P.t1) continue;
    const u = (t - P.t0) / (P.t1 - P.t0), d = P.dir, x = Math.round(d > 0 ? lerp(-40, W + 24, u) : lerp(W + 40, -44, u)), sx = x - d * 19, fy = LANE_RUN + 1;
    ctx.fillStyle = 'rgba(16,20,48,0.3)'; ctx.fillRect(sx - 6, fy + 1, 13, 1);
    person(sx - d, fy - 2, L_RIDER, { view: 2, dir: d, sit: true, aR: hash(step(t, 120), 9) < 0.3 ? 1 : 0, snow });   // the child, waving now and then
    R(sx - 6, fy - 2, 12, 3, '#b8263a'); R(sx - 6, fy - 2, 12, 1, '#e8505a'); R(d > 0 ? sx + 5 : sx - 7, fy - 4, 2, 3, '#c8283a');   // the red pulka, its nose curled up
    const hy = tyOf(L_PULL, fy) + L_PULL.th - 2, hx = x - d, fx = d > 0 ? sx + 6 : sx - 7, n = Math.abs(hx - fx);
    ctx.fillStyle = '#d8d0b0';
    for (let k = 1; k < n; k++) ctx.fillRect(hx - d * k, Math.round(lerp(hy, fy - 3, k / n) + Math.sin(Math.PI * k / n) * 1.5), 1, 1);
    person(x, fy, L_PULL, { view: 2, dir: d, step: Math.floor(x / 4) & 1, snow });
  }
}
function drawTerrace(t, crowd, snow) {
  blit(TERR_C);
  drawSnowSparks(t);
  const DL = [], put = (fy, fn) => DL.push([fy, fn]), here = rank => !nobody(crowd) && (rank === 0 || rank < crowd);
  put(STATUE.fy + 14, () => { halo(HL.statue, STATUE.x, STATUE.fy - 10, 0.9); ctx.drawImage(STATUE_C, STATUE.x - 15, STATUE.fy - 25); drawStatueGull(t); });
  for (const L of LAMPS) put(L.y + 87, () => ctx.drawImage(LAMP_C, L.x - 6, L.y - 10));
  put(BENCH.y, () => { if (here(RANK.bench)) drawSitter(t, snow); ctx.drawImage(BENCH_C, BENCH.x, BENCH.y - 15); });
  if (here(RANK.couple)) put(EDGE, () => drawCouple(t, snow));
  if (here(RANK.kid)) put(EDGE + 1, () => drawKid(t, snow));
  if (here(RANK.photo)) put(EDGE + 1.5, () => drawPhoto(t, snow));
  if (here(RANK.dog)) put(LANE_DOG, () => drawDogWalker(t, snow));
  if (here(RANK.jog)) put(LANE_RUN, () => drawJogger(t, snow));
  if (here(RANK.pulka)) put(LANE_RUN + 1, () => drawPulka(t, snow));
  put(SNOWMAN.fy, () => { ctx.fillStyle = 'rgba(16,20,48,0.3)'; ctx.fillRect(SNOWMAN.x - 6, SNOWMAN.fy + 1, 13, 1); ctx.drawImage(SNOWMAN_C, SNOWMAN.x - 8, SNOWMAN.fy - 25); });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  for (const L of LAMPS) { const I = 0.94 + 0.04 * wave(t, 900, L.x * 0.01); halo(HL.lamp, L.x, L.y + 2, I); halo(HL.lampCore, L.x, L.y, I); }
}

/* ================= The snow ================= */
function drawSnow(L, t, lights) {
  const span = W + 40;
  for (const f of L.flakes) {
    const { cyc, u } = cycle(t, f.n, f.ph);
    const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p));
    x = ((x % span) + span) % span - 20;
    let col = L.col;
    if (L.lights) for (const [cx, cy, rx, ry, lc] of lights) { const dx = (x - cx) / rx, dy = (y - cy) / ry; if (dx * dx + dy * dy < 1) { col = lc; break; } }
    ctx.globalAlpha = L.y1[0] < H && u > 0.9 ? (1 - u) * 10 : 1;
    ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the neon sign; billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the people
// to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawSkyGulls(t);
  drawFar(t);
  drawSod(t, crowd);
  if (billboard) drawBoard(t, pitch);
  if (wx.snow) drawSnow(SNOW_L[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawTowerGulls(t, false);
  drawHall(t);
  drawRight(t, crowd);
  drawTowerGulls(t, true);
  drawWater(t, billboard);
  drawBoats(t, crowd);
  if (wx.snow) drawSnow(SNOW_L[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawTerrace(t, crowd, !!wx.snow);
  if (wx.snow) drawSnow(SNOW_L[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'stockholm',
  name: 'Stockholm',
  tagline: 'Advent snow over Stadshuset',
  timeZone: 'Europe/Stockholm',
  accent: '#5fb3a0',                                   // Stadshuset's copper verdigris, written out: tools/seo/seo.mjs reads it from the source
  focusX: T.cx,
  landmark: [SWING.x0, EWING.x1],                      // Stadshuset: the south wing's far end to the east wing's, the tower between
  landmarkTop: T.top,                                  // the row of the top crown
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { wind: 0.3, water: 0.25, traffic: 0.12, crowd: 0.06 },
  events: [
    { t: FERRY[0].t0 + 1, type: 'horn', x: 40, gain: 0.22 },
    { t: FERRY[0].t0 + 1.5, type: 'boat', dur: 50, pan: [-0.9, 0.9], gain: 0.28 },
    { t: 24, type: 'gull', x: 330, gain: 0.32 },
    { t: STEAMER.horn, type: 'horn', x: 440, gain: 0.45 },
    { t: STEAMER.horn + 1, type: 'boat', dur: 86, pan: [0.9, -0.9], gain: 0.32 },
    { t: GULL_SIT.land - 1, type: 'gull', x: STATUE.x, gain: 0.45 },
    { t: FERRY[1].t0 + 1, type: 'horn', x: 440, gain: 0.22 },
    { t: FERRY[1].t0 + 1.5, type: 'boat', dur: 50, pan: [0.9, -0.9], gain: 0.28 },
    { t: GULL_SIT.off, type: 'gull', x: STATUE.x, gain: 0.4 },
    ...BELLS.map(t => ({ t, type: 'bell', x: T.cx, gain: 0.5 })),
    { t: 212, type: 'gull', x: 200, gain: 0.3 },
    { t: KISS[0] + 0.5, type: 'chime', x: COUPLE.x, gain: 0.28 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
